import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import { useDirectReports, useShifts, useShiftRequests, useNotifications, useSchedule, useInvites, useAvailability, useToast, useAnalytics, useScheduleHistory, useDepartments } from '../hooks/useData';
import { Btn, Card, Badge, Avatar, StatCard, Modal, FormRow, Input, Select, EmptyState, PageHeader, Toast, Spinner, LoadingScreen, DepartmentBadge } from '../components/UI';
import { fmtDate, fmtTime, getWeekStart, addDays, toISO, buildWeek, fmtWeekRange, colorFor } from '../utils/dates';
import { supabase } from '../lib/supabase';
import '../components/UI.css';

function planAtLeast(orgPlan, required) {
  const ORDER = { trial: 0, starter: 1, growth: 2, enterprise: 3 };
  return (ORDER[orgPlan] ?? -1) >= (ORDER[required] ?? 0);
}

function UpgradePrompt({ requiredPlan }) {
  const navigate = useNavigate();
  const label = requiredPlan.charAt(0).toUpperCase() + requiredPlan.slice(1);
  return (
    <div className="page">
      <PageHeader title="Feature not available" />
      <EmptyState
        icon="🔒"
        title={`${label} plan required`}
        message={`This feature is available on the ${label} plan and above.`}
        action={<Btn onClick={() => navigate('/billing')}>View plans</Btn>}
      />
    </div>
  );
}

const AVAIL_DAYS = [
  { label: 'Monday',    dow: 1 },
  { label: 'Tuesday',   dow: 2 },
  { label: 'Wednesday', dow: 3 },
  { label: 'Thursday',  dow: 4 },
  { label: 'Friday',    dow: 5 },
  { label: 'Saturday',  dow: 6 },
  { label: 'Sunday',    dow: 0 },
];

// Read-only availability grid shown in a manager's modal
function EmployeeAvailabilityView({ employeeId }) {
  const { availability, loading } = useAvailability(employeeId);
  if (loading) return <Spinner />;
  const map = {};
  availability.forEach(a => { map[a.day_of_week] = a; });
  return (
    <div>
      {AVAIL_DAYS.map(({ label, dow }) => {
        const a = map[dow];
        return (
          <div key={dow} style={{ display:'flex', alignItems:'center', padding:'10px 0', borderBottom:'1px solid var(--border)' }}>
            <span style={{ width:110, fontSize:14, fontWeight:500 }}>{label}</span>
            {a?.is_available
              ? <span style={{ fontSize:13, color:'var(--green)', fontWeight:500 }}>{fmtTime(a.start_time || '09:00:00')} – {fmtTime(a.end_time || '17:00:00')}</span>
              : <span style={{ fontSize:13, color:'var(--muted)' }}>Unavailable</span>
            }
          </div>
        );
      })}
      {availability.length === 0 && <p style={{ fontSize:13, color:'var(--muted)', paddingTop:8 }}>No availability set yet.</p>}
    </div>
  );
}
// ── Manager Dashboard ─────────────────────────────────────────────────────────
export function ManagerDashboard() {
  const { isOwner } = useAuth();
  if (isOwner) return <OwnerDashboard />;
  return <ManagerDashboardInner />;
}

function OwnerDashboard() {
  const { org, profile } = useAuth();
  const weekStart = toISO(getWeekStart(new Date()));
  const weekEnd   = toISO(addDays(getWeekStart(new Date()), 6));
  const { shifts } = useShifts(weekStart, weekEnd);
  const [profiles, setProfiles]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [expanded, setExpanded]   = useState({});

  useEffect(() => {
    if (!org) return;
    supabase.from('profiles').select('id,name,role,position,reports_to,avatar_color,avatar_text_color')
      .eq('org_id', org.id).eq('is_active', true)
      .then(({ data }) => { setProfiles(data || []); setLoading(false); });
  }, [org]);

  if (loading) return <LoadingScreen />;

  const managers   = profiles.filter(p => p.role === 'manager');
  const employees  = profiles.filter(p => p.role === 'employee');

  // Build team cards: one per manager + one "My team" for the owner
  const allTeams = [
    { id: profile.id, label: "My team", isOwner: true, avatar: profile },
    ...managers.map(m => ({ id: m.id, label: `${m.name}'s team`, isOwner: false, avatar: m })),
  ];

  const teamCards = allTeams.map(t => {
    const members = profiles.filter(p => p.reports_to === t.id);
    const teamShifts = shifts.filter(s => members.some(m => m.id === s.employee_id));
    const shiftMap = {};
    teamShifts.forEach(s => { shiftMap[s.employee_id] = (shiftMap[s.employee_id] || 0) + 1; });
    return { ...t, members, shiftCount: teamShifts.length, shiftMap };
  }).filter(t => t.isOwner || t.members.length > 0);

  function toggleExpand(id) { setExpanded(e => ({ ...e, [id]: !e[id] })); }

  return (
    <div className="page">
      <PageHeader title="Organization overview" subtitle={new Date().toLocaleDateString('en-US', { weekday:'long', year:'numeric', month:'long', day:'numeric' })} />
      <div className="stats-grid" style={{ marginBottom: 20 }}>
        <StatCard label="Total employees"  value={employees.length} sub="Active" />
        <StatCard label="Managers"         value={managers.length}  sub="Active" />
        <StatCard label="Shifts this week" value={shifts.length}    sub="Across all teams" />
      </div>
      <div style={{ fontSize:13, fontWeight:600, color:'var(--muted)', textTransform:'uppercase', letterSpacing:0.5, marginBottom:10 }}>Teams at a glance</div>
      {teamCards.length === 0
        ? <EmptyState icon="👥" title="No teams yet" message="Invite managers from the Employees tab to build your team structure." />
        : teamCards.map(t => (
          <Card key={t.id} style={{ marginBottom:10, overflow:'hidden' }}>
            <div
              onClick={() => toggleExpand(t.id)}
              style={{ display:'flex', alignItems:'center', gap:12, padding:'14px 16px', cursor:'pointer', userSelect:'none' }}
            >
              <Avatar name={t.avatar.name} color={t.avatar.avatar_color} textColor={t.avatar.avatar_text_color} size="md" />
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:700, fontSize:15 }}>{t.label}</div>
                <div style={{ fontSize:12, color:'var(--muted)' }}>{t.members.length} member{t.members.length !== 1 ? 's' : ''} · {t.shiftCount} shift{t.shiftCount !== 1 ? 's' : ''} this week</div>
              </div>
              <span style={{ fontSize:13, color:'var(--muted)' }}>{expanded[t.id] ? '▲' : '▼'}</span>
            </div>
            {expanded[t.id] && (
              <div style={{ borderTop:'1px solid var(--border)' }}>
                {t.members.length === 0
                  ? <div style={{ padding:'12px 16px', fontSize:13, color:'var(--muted)' }}>No members assigned to this team yet.</div>
                  : t.members.map(m => (
                    <div key={m.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 16px', borderBottom:'1px solid var(--border)' }}>
                      <Avatar name={m.name} color={m.avatar_color} textColor={m.avatar_text_color} size="sm" />
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:13.5, fontWeight:500 }}>{m.name}</div>
                        <div style={{ fontSize:12, color:'var(--muted)' }}>{m.position || m.role}</div>
                      </div>
                      <div style={{ fontSize:13, color:'var(--muted)' }}>{t.shiftMap[m.id] || 0} shift{(t.shiftMap[m.id] || 0) !== 1 ? 's' : ''}</div>
                    </div>
                  ))
                }
              </div>
            )}
          </Card>
        ))
      }
    </div>
  );
}

function ManagerDashboardInner() {
  const { org, profile } = useAuth();
  const { toast, show } = useToast();
  const weekStart = toISO(getWeekStart(new Date()));
  const weekEnd   = toISO(addDays(getWeekStart(new Date()), 6));
  const { reports: members, loading: empLoading } = useDirectReports();
  const { shifts } = useShifts(weekStart, weekEnd);
  const { requests, reload: reloadReq } = useShiftRequests();
  const { notifications } = useNotifications();

  const pending = requests.filter(r => r.status === 'pending');

  async function handleReview(requestId, decision) {
    const req = requests.find(r => r.id === requestId);
    const shift = req?.available_shift?.shift;
    await supabase.from('shift_requests').update({ status: decision, reviewed_by: profile.id, reviewed_at: new Date().toISOString() }).eq('id', requestId);
    if (decision === 'approved' && shift) {
      await supabase.from('shifts').update({ employee_id: req.requester_id }).eq('id', shift.id);
      await supabase.from('available_shifts').update({ is_open: false }).eq('id', req.available_shift_id);
    }
    await supabase.from('notifications').insert({ org_id: org.id, user_id: req.requester_id, text: `Your shift request for ${shift?.position} on ${shift?.shift_date} was ${decision}.` });
    show(`Request ${decision}!`, decision === 'approved' ? 'success' : 'error');
    reloadReq();
  }

  if (empLoading) return <LoadingScreen />;

  return (
    <div className="page">
      <Toast toast={toast} onClose={() => {}} />
      <PageHeader title="Manager overview" subtitle={new Date().toLocaleDateString('en-US', { weekday:'long', year:'numeric', month:'long', day:'numeric' })} />
      <div className="stats-grid">
        <StatCard label="Team members"     value={members.length}                              sub="Managers + employees" />
        <StatCard label="Shifts this week" value={shifts.length}                               sub="Scheduled" />
        <StatCard label="Pending requests" value={pending.length} color={pending.length>0?'var(--amber)':undefined} sub="Need review" />
        <StatCard label="Unread alerts"    value={notifications.filter(n=>!n.read).length}    color="var(--red)" sub="Notifications" />
      </div>
      <div className="grid-2">
        <Card>
          <div className="card-header"><div className="card-title">Pending requests</div><Badge status="pending" /></div>
          {pending.length === 0
            ? <EmptyState icon="✅" title="All clear" message="No pending requests." />
            : pending.map(r => {
              const shift = r.available_shift?.shift;
              return (
                <div key={r.id} className="list-item">
                  <Avatar name={r.requester?.name} color={r.requester?.avatar_color} textColor={r.requester?.avatar_text_color} size="sm" />
                  <div className="list-info">
                    <div className="list-title">{r.requester?.name} → {shift?.position}</div>
                    <div className="list-meta">{fmtDate(shift?.shift_date)} · {fmtTime(shift?.start_time)}–{fmtTime(shift?.end_time)}</div>
                  </div>
                  <div style={{display:'flex',gap:6,flexShrink:0}}>
                    <Btn size="sm" variant="success" onClick={() => handleReview(r.id,'approved')}>✓</Btn>
                    <Btn size="sm" variant="danger"  onClick={() => handleReview(r.id,'denied')}>✕</Btn>
                  </div>
                </div>
              );
            })
          }
        </Card>
        <Card>
          <div className="card-header"><div className="card-title">Your team</div></div>
          {members.slice(0,6).map(e => (
            <div key={e.id} className="list-item">
              <Avatar name={e.name} color={e.avatar_color} textColor={e.avatar_text_color} size="sm" />
              <div className="list-info">
                <div className="list-title">{e.name}</div>
                <div className="list-meta">{e.role === 'manager' ? 'Manager' : e.position} · {e.department}</div>
              </div>
              <Badge status="approved" />
            </div>
          ))}
          {members.length === 0 && <EmptyState icon="👥" title="No team members yet" message="Add team members from the Employees tab." />}
        </Card>
      </div>
    </div>
  );
}

// ── Schedule Builder ──────────────────────────────────────────────────────────
export function ScheduleBuilder() {
  const { org, profile, isOwner } = useAuth();
  const { reports: ownDirectReports } = useDirectReports();
  const { departments } = useDepartments();
  const { toast, show } = useToast();
  const navigate = useNavigate();
  const [weekStart, setWeekStart]         = useState(() => getWeekStart(new Date()));
  const weekStartISO = toISO(weekStart);
  const weekEndISO   = toISO(addDays(weekStart, 6));
  const week = buildWeek(weekStart);
  const isPastWeek = weekStartISO < toISO(getWeekStart(new Date()));
  const { shifts, reload: reloadShifts } = useShifts(weekStartISO, weekEndISO);
  const { schedule, ensureSchedule, publish } = useSchedule(weekStartISO);
  const [addModal, setAddModal]           = useState(false);
  const [prefillDate, setPrefill]         = useState('');
  const [confirmDel, setConfirmDel]       = useState(null);
  const [saving, setSaving]               = useState(false);
  const [confirmGenModal, setConfirmGenModal] = useState(false);
  const [noNeedsModal, setNoNeedsModal]       = useState(false);
  const [generating, setGenerating]           = useState(false);
  const [viewingManagerId, setViewingManagerId] = useState(null);
  const [viewingTeam, setViewingTeam]     = useState([]);
  const [tSlots, setTSlots]               = useState([]);
  const [tDays, setTDays]                 = useState([1,2,3,4,5]);
  const [loadedForManagerId, setLoadedForManagerId] = useState(null);
  const [form, setForm] = useState({ employeeId:'', date:'', startTime:'', endTime:'', position:'', department:'', departmentId:'' });
  const [availWarn, setAvailWarn] = useState('');
  const [overtimeWarn, setOvertimeWarn] = useState('');
  const [copying, setCopying] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [clearConfirm, setClearConfirm] = useState(false);

  // Which manager's template to load: the one being viewed, or self
  const effectiveManagerId = viewingManagerId || profile?.id;

  useEffect(() => {
    if (!viewingManagerId) { setViewingTeam([]); return; }
    supabase.from('profiles').select('*')
      .eq('org_id', org.id).eq('reports_to', viewingManagerId)
      .eq('is_active', true).order('name')
      .then(({ data }) => setViewingTeam(data || []));
  }, [viewingManagerId, org]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!org || !profile || !effectiveManagerId) return;
    if (loadedForManagerId === effectiveManagerId) return;
    supabase.from('schedule_templates').select('*')
      .eq('org_id', org.id).eq('manager_id', effectiveManagerId).maybeSingle()
      .then(({ data }) => {
        if (data) { setTSlots(data.slots || []); setTDays(data.days || [1,2,3,4,5]); }
        else { setTSlots([]); setTDays([1,2,3,4,5]); }
        setLoadedForManagerId(effectiveManagerId);
      });
  }, [org, profile, effectiveManagerId, loadedForManagerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const employees = viewingManagerId ? viewingTeam : ownDirectReports;

  function snapTo15(t) {
    if (!t) return t;
    const [h, m] = t.split(':').map(Number);
    const snapped = Math.round(m / 15) * 15;
    return `${String(h + (snapped === 60 ? 1 : 0)).padStart(2,'0')}:${String(snapped % 60).padStart(2,'0')}`;
  }

  // Warn if selected employee is unavailable for the chosen day/time
  useEffect(() => {
    if (!form.employeeId || !form.date) { setAvailWarn(''); return; }
    const dow = new Date(form.date + 'T12:00:00').getDay();
    supabase.from('availability').select('*')
      .eq('employee_id', form.employeeId).eq('day_of_week', dow).maybeSingle()
      .then(({ data: a }) => {
        const emp = employees.find(e => e.id === form.employeeId);
        const name = emp?.name?.split(' ')[0] || 'This employee';
        const days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
        if (!a) { setAvailWarn(''); return; }
        if (!a.is_available) { setAvailWarn(`${name} is marked unavailable on ${days[dow]}s.`); return; }
        if (form.startTime && a.start_time && form.endTime && a.end_time) {
          if (form.startTime < a.start_time.slice(0,5) || form.endTime > a.end_time.slice(0,5)) {
            setAvailWarn(`${name}'s availability on ${days[dow]}s is ${fmtTime(a.start_time)} – ${fmtTime(a.end_time)}.`);
            return;
          }
        }
        setAvailWarn('');
      });
  }, [form.employeeId, form.date, form.startTime, form.endTime]); // eslint-disable-line react-hooks/exhaustive-deps

  // Overtime detection: warn if new shift would push employee near/over their weekly limit
  useEffect(() => {
    if (!form.employeeId || !form.startTime || !form.endTime) { setOvertimeWarn(''); return; }
    const [sh, sm] = form.startTime.split(':').map(Number);
    const [eh, em] = form.endTime.split(':').map(Number);
    const newHrs = Math.max(0, (eh * 60 + em) - (sh * 60 + sm)) / 60;
    const existingHrs = displayedShifts
      .filter(s => s.employee_id === form.employeeId)
      .reduce((sum, s) => sum + (s.duration_hours || 0), 0);
    const total = existingHrs + newHrs;
    const emp = employees.find(e => e.id === form.employeeId);
    const name = emp?.name?.split(' ')[0] || 'This employee';
    const limit = emp?.max_weekly_hours ?? 40;
    if (total > limit) {
      setOvertimeWarn(`${name} would be at ${total.toFixed(1)}h this week — exceeds the ${limit}h limit.`);
    } else if (total >= limit - 4) {
      setOvertimeWarn(`Heads up: ${name} will have ${total.toFixed(1)}h this week after this shift.`);
    } else {
      setOvertimeWarn('');
    }
  }, [form.employeeId, form.startTime, form.endTime]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleCopyLastWeek() {
    setCopying(true);
    try {
      const prevStart = toISO(addDays(weekStart, -7));
      const prevEnd   = toISO(addDays(weekStart, -1));
      const { data: prevShifts } = await supabase.from('shifts').select('*')
        .eq('org_id', org.id).gte('shift_date', prevStart).lte('shift_date', prevEnd);
      if (!prevShifts?.length) { show('No shifts found from last week.', 'error'); return; }
      const existingKeys = new Set(shifts.map(s => `${s.employee_id}_${s.shift_date}`));
      const sched = await ensureSchedule(profile.id);
      const newShifts = prevShifts
        .map(s => {
          const newDate = toISO(addDays(new Date(s.shift_date + 'T12:00:00'), 7));
          return { org_id: org.id, schedule_id: sched.id, employee_id: s.employee_id, shift_date: newDate, start_time: s.start_time, end_time: s.end_time, position: s.position, department: s.department };
        })
        .filter(s => !existingKeys.has(`${s.employee_id}_${s.shift_date}`));
      if (!newShifts.length) { show('All last week\'s shifts already exist this week.', 'error'); return; }
      await supabase.from('shifts').insert(newShifts);
      show(`Copied ${newShifts.length} shift${newShifts.length > 1 ? 's' : ''} from last week!`);
      reloadShifts();
    } catch (err) { show(err.message, 'error'); }
    finally { setCopying(false); }
  }

  function handleClearAll() {
    const todayStr = toISO(new Date());
    // Only clear shifts visible in the current team view
    const removable = displayedShifts.filter(s => s.shift_date >= todayStr);
    if (!removable.length) return show('No upcoming shifts to clear this week.', 'error');
    setClearConfirm(true);
  }

  async function handleClearConfirmed() {
    const todayStr = toISO(new Date());
    const removable = displayedShifts.filter(s => s.shift_date >= todayStr);
    setClearConfirm(false);
    setClearing(true);
    try {
      await supabase.from('shifts').delete().eq('org_id', org.id).in('id', removable.map(s => s.id));
      show(`Cleared ${removable.length} shift${removable.length > 1 ? 's' : ''}.`);
      reloadShifts();
    } catch (err) { show(err.message, 'error'); }
    finally { setClearing(false); }
  }

  function openAdd(date='') { if (isPastWeek) return; setPrefill(date); setAvailWarn(''); setOvertimeWarn(''); setForm(f=>({...f,date,employeeId:'',startTime:'',endTime:'',position:'',department:'',departmentId:''})); setAddModal(true); }

  async function handleAdd(e) {
    e.preventDefault(); setSaving(true);
    try {
      const startTime = snapTo15(form.startTime);
      const endTime   = snapTo15(form.endTime);
      if (new Date(`${form.date}T${startTime}`) <= new Date()) {
        show('Cannot add a shift that has already started or passed.', 'error');
        return;
      }
      const sched = await ensureSchedule(profile.id);
      const emp = employees.find(em => em.id === form.employeeId);
      await supabase.from('shifts').insert({ org_id: org.id, schedule_id: sched.id, employee_id: form.employeeId, shift_date: form.date, start_time: startTime, end_time: endTime, position: form.position || emp?.position, department: form.department || emp?.department, ...(form.departmentId ? { department_id: form.departmentId } : {}) });
      await supabase.from('notifications').insert({ org_id: org.id, user_id: form.employeeId, text: `A new shift has been added: ${form.position} on ${form.date}.` });
      show('Shift added!');
      setAddModal(false);
      reloadShifts();
    } catch (err) { show(err.message, 'error'); }
    finally { setSaving(false); }
  }

  async function handleRemove() {
    if (!confirmDel) return;
    if (confirmDel.shift_date < toISO(new Date())) { setConfirmDel(null); return; }
    await supabase.from('shifts').delete().eq('id', confirmDel.id);
    show('Shift removed.');
    setConfirmDel(null);
    reloadShifts();
  }

  async function handlePublish() {
    await publish(employees.map(e => e.id));
    show('Schedule published! Team notified.');
  }

  // week indices 0-6 map to dow: Mon=1, Tue=2, ..., Sun=0
  const DOW_FROM_IDX = [1,2,3,4,5,6,0];

  async function handleAutoGenerate() {
    setGenerating(true);
    try {
      await supabase.from('schedule_templates').upsert({
        org_id: org.id, manager_id: effectiveManagerId,
        slots: tSlots, days: tDays, updated_at: new Date().toISOString(),
      }, { onConflict: 'org_id,manager_id' });

      const empIds = employees.map(e => e.id);

      const [availRes, deptRes] = await Promise.all([
        // Fetch all availability rows (true AND false) so we can distinguish
        // "no record" (treat as available) from "explicitly unavailable"
        supabase.from('availability').select('employee_id, day_of_week, is_available')
          .in('employee_id', empIds),
        empIds.length > 0
          ? supabase.from('employee_departments')
              .select('employee_id, department_id, departments(id, is_any)')
              .in('employee_id', empIds)
          : Promise.resolve({ data: [] }),
      ]);

      const availMap = {};
      (availRes.data || []).forEach(a => { availMap[`${a.employee_id}_${a.day_of_week}`] = a; });

      // Map: employeeId → [{ department_id, is_any }]
      const empDeptMap = {};
      (deptRes.data || []).forEach(ed => {
        if (!empDeptMap[ed.employee_id]) empDeptMap[ed.employee_id] = [];
        empDeptMap[ed.employee_id].push({ department_id: ed.department_id, is_any: ed.departments?.is_any });
      });

      const existingKeys = new Set(shifts.map(s => `${s.employee_id}_${s.shift_date}`));
      const sched = await ensureSchedule(profile.id);
      const newShifts = [];
      let shortfalls = 0;
      const runAssignments = {}; // tracks shifts assigned per employee across the full week run

      week.forEach(({ date }, idx) => {
        const dow = DOW_FROM_IDX[idx];
        if (!tDays.includes(dow)) return;
        tSlots.forEach(slot => {
          const hasPosition   = slot.position?.trim();
          const hasDepartment = slot.department_id;
          if (!hasPosition && !hasDepartment) return;
          if (slot.count < 1) return;

          const eligible = employees
            .filter(emp => {
              // Skip non-active employees
              if (emp.status && emp.status !== 'active') return false;
              // Department matching (if slot has department_id)
              if (hasDepartment) {
                const empDepts = empDeptMap[emp.id] || [];
                const match = empDepts.some(d => d.department_id === slot.department_id || d.is_any);
                if (!match) return false;
              } else {
                // Fallback: position-based matching for legacy slots
                if (emp.position?.toLowerCase() !== hasPosition.toLowerCase()) return false;
              }
              // No availability record = available by default (managers/new employees rarely fill this out)
              // Only exclude if there's an explicit is_available = false record
              const availEntry = availMap[`${emp.id}_${dow}`];
              const isAvail = availEntry ? availEntry.is_available !== false : true;
              return isAvail && !existingKeys.has(`${emp.id}_${date}`);
            })
            // 3-level sort: below minimum → fewest assigned so far → priority tiebreak
            .sort((a, b) => {
              const aCount = runAssignments[a.id] ?? 0;
              const bCount = runAssignments[b.id] ?? 0;
              const aBelowMin = aCount < (a.min_weekly_shifts ?? 0) ? 1 : 0;
              const bBelowMin = bCount < (b.min_weekly_shifts ?? 0) ? 1 : 0;
              if (bBelowMin !== aBelowMin) return bBelowMin - aBelowMin;
              if (aCount !== bCount) return aCount - bCount;
              return (b.scheduling_priority ?? 50) - (a.scheduling_priority ?? 50);
            });

          const toAssign = eligible.slice(0, slot.count);
          shortfalls += Math.max(0, slot.count - toAssign.length);
          toAssign.forEach(emp => {
            newShifts.push({
              org_id: org.id, schedule_id: sched.id,
              employee_id: emp.id, shift_date: date,
              start_time: slot.startTime, end_time: slot.endTime,
              position: slot.position || '',
              department: slot.departmentName || emp.department || 'General',
              ...(slot.department_id ? { department_id: slot.department_id } : {}),
            });
            existingKeys.add(`${emp.id}_${date}`);
            runAssignments[emp.id] = (runAssignments[emp.id] ?? 0) + 1;
          });
        });
      });

      if (newShifts.length === 0) {
        show('No shifts generated. Check that your team needs slots have matching sections or positions, and that team members aren\'t already scheduled.', 'error');
        return;
      }

      await supabase.from('shifts').insert(newShifts);
      const byEmp = {};
      newShifts.forEach(s => { (byEmp[s.employee_id] = byEmp[s.employee_id] || []).push(s); });
      const notifs = Object.entries(byEmp).map(([uid, ss]) => ({
        org_id: org.id, user_id: uid,
        text: `${ss.length} shift${ss.length > 1 ? 's have' : ' has'} been auto-scheduled for you this week.`,
      }));
      if (notifs.length > 0) await supabase.from('notifications').insert(notifs);

      const warn = shortfalls > 0 ? ` (${shortfalls} slot${shortfalls > 1 ? 's' : ''} couldn't be filled — not enough available employees with matching positions)` : '';
      show(`Generated ${newShifts.length} shifts!${warn}`);
      setConfirmGenModal(false);
      reloadShifts();
    } catch (err) { show(err.message, 'error'); }
    finally { setGenerating(false); }
  }

  const teamIds = new Set(employees.map(e => e.id));
  const displayedShifts = shifts.filter(s => teamIds.has(s.employee_id));
  const shiftsByDate = {};
  displayedShifts.forEach(s => { (shiftsByDate[s.shift_date] = shiftsByDate[s.shift_date] || []).push(s); });
  const todayISO = toISO(new Date());
  const canExportCSV = planAtLeast(org?.plan, 'growth');

  // Coverage: scheduled vs. required per slot-group for this week
  const coverageData = (() => {
    if (!tSlots.length || !loadedForManagerId) return [];
    const valid = tSlots.filter(s => s.department_id || s.position?.trim());
    if (!valid.length) return [];
    const map = {};
    valid.forEach(slot => {
      const key = slot.department_id || `pos_${slot.position}`;
      if (!map[key]) {
        const dept = departments.find(d => d.id === slot.department_id);
        map[key] = { key, label: slot.departmentName || slot.position || 'Slot', color: dept?.color || '#6366f1', needed: 0, filled: 0 };
      }
      week.forEach(({ date }, idx) => {
        if (!tDays.includes(DOW_FROM_IDX[idx])) return;
        map[key].needed += slot.count;
        const dayFilled = displayedShifts.filter(s => {
          if (s.shift_date !== date) return false;
          return slot.department_id
            ? s.department_id === slot.department_id
            : s.position?.toLowerCase() === slot.position?.toLowerCase();
        }).length;
        map[key].filled += Math.min(dayFilled, slot.count);
      });
    });
    return Object.values(map);
  })();

  function exportCSV() {
    const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
    const header = ['Employee','Date','Day','Start','End','Hours','Position','Department'];
    const rows = displayedShifts.map(s => [
      s.employee?.name || '',
      s.shift_date,
      DAYS[new Date(s.shift_date + 'T12:00:00').getDay()],
      fmtTime(s.start_time),
      fmtTime(s.end_time),
      s.duration_hours ?? '',
      s.position || '',
      s.department || '',
    ]);
    const csv = [header, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(blob), download: `schedule-${weekStartISO}.csv` });
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <div className="page">
      <Toast toast={toast} />
      <PageHeader title="Schedule builder"
        action={
          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            <Btn size="sm" onClick={() => openAdd()} disabled={isPastWeek}>+ Add shift</Btn>
            <Btn size="sm" onClick={handleCopyLastWeek} disabled={copying || isPastWeek}>{copying ? <Spinner /> : 'Copy last week'}</Btn>
            <Btn size="sm" onClick={handleClearAll} disabled={clearing || isPastWeek} style={{ color:'#e03131', borderColor:'#e03131', background:'transparent' }}>{clearing ? <Spinner /> : 'Clear all'}</Btn>
            <Btn size="sm" onClick={() => tSlots.some(s => s.position?.trim() || s.department_id) ? setConfirmGenModal(true) : setNoNeedsModal(true)} disabled={isPastWeek}>Auto-generate</Btn>
            <span title={!canExportCSV ? 'Upgrade to Growth to export CSV' : undefined}>
              <Btn size="sm" onClick={exportCSV} disabled={!canExportCSV || displayedShifts.length === 0}>Export CSV</Btn>
            </span>
            {isPastWeek
              ? <span style={{fontSize:12,padding:'5px 10px',borderRadius:'var(--radius-sm)',background:'var(--bg)',border:'1px solid var(--border)',color:'var(--muted)',display:'inline-flex',alignItems:'center'}}>{schedule?.published ? '✓ Published' : 'Not published'}</span>
              : <Btn size="sm" variant="primary" onClick={handlePublish}>{schedule?.published ? '✓ Published' : 'Publish schedule'}</Btn>
            }
          </div>
        }
      />
      {isOwner && ownDirectReports.length > 0 && (
        <div style={{marginBottom:12,display:'flex',alignItems:'center',gap:8}}>
          <span style={{fontSize:13,color:'var(--muted)',fontWeight:500}}>Viewing:</span>
          <Select value={viewingManagerId || ''} onChange={e => setViewingManagerId(e.target.value || null)}
            style={{width:'auto',minWidth:200}}>
            <option value="">My direct reports</option>
            {ownDirectReports.map(m => <option key={m.id} value={m.id}>{m.name}'s team</option>)}
          </Select>
        </div>
      )}
      {isPastWeek && (
        <div style={{background:'var(--bg)',border:'1px solid var(--border-md)',borderRadius:'var(--radius-sm)',padding:'10px 16px',marginBottom:16,display:'flex',alignItems:'center',gap:10,fontSize:13.5,color:'var(--muted)'}}>
          <span>🔒</span>
          <span>This week has already passed — the schedule is view-only.</span>
          <Btn size="sm" style={{marginLeft:'auto',flexShrink:0}} onClick={() => setWeekStart(getWeekStart(new Date()))}>Go to current week</Btn>
        </div>
      )}
      <div className="week-nav">
        <div className="week-nav-label">{fmtWeekRange(weekStart)}</div>
        <div className="week-nav-btns">
          <Btn size="sm" onClick={() => setWeekStart(d => addDays(d,-7))}>← Prev</Btn>
          <Btn size="sm" onClick={() => setWeekStart(getWeekStart(new Date()))}>Today</Btn>
          <Btn size="sm" onClick={() => setWeekStart(d => addDays(d,7))}>Next →</Btn>
        </div>
      </div>
      {coverageData.length > 0 && (
        <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:12}}>
          {coverageData.map(c => {
            const pct = c.needed ? c.filled / c.needed * 100 : 0;
            const barColor = pct >= 100 ? 'var(--green)' : pct >= 60 ? 'var(--amber)' : 'var(--red)';
            return (
              <div key={c.key} style={{display:'flex',alignItems:'center',gap:8,padding:'7px 12px',borderRadius:'var(--radius-sm)',background:'var(--surface)',border:'1px solid var(--border)',flex:'0 1 auto',minWidth:130}}>
                <div style={{width:8,height:8,borderRadius:'50%',background:c.color,flexShrink:0}} />
                <div style={{flex:1}}>
                  <div style={{fontSize:11.5,fontWeight:600,color:'var(--text)',marginBottom:3,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:120}}>{c.label}</div>
                  <div style={{display:'flex',alignItems:'center',gap:6}}>
                    <div style={{flex:1,height:3,background:'var(--border)',borderRadius:2,minWidth:60}}>
                      <div style={{width:`${Math.min(100,pct)}%`,height:'100%',background:barColor,borderRadius:2,transition:'width 0.4s'}} />
                    </div>
                    <span style={{fontSize:10,color:'var(--muted)',whiteSpace:'nowrap'}}>{c.filled}/{c.needed}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <Card style={{marginBottom:16,padding:'18px 16px'}}>
        <div className="schedule-scroll">
          <div className="week-grid">
            {week.map(({ key, date, num }) => {
              const dayShifts = shiftsByDate[date] || [];
              return (
                <div key={key} className="day-col"
                  onClick={!isPastWeek ? () => openAdd(date) : undefined}
                  style={isPastWeek ? {cursor:'default'} : undefined}
                >
                  <div className="day-header">{key}</div>
                  <div className={`day-date ${date===todayISO?'today':''}`}>{num}</div>
                  {dayShifts.length === 0
                    ? <div className="day-off">No shifts</div>
                    : dayShifts.map(s => {
                      const dept = departments.find(d => d.id === s.department_id && !d.is_any)
                        || departments.find(d => d.name === s.department && !d.is_any);
                      return (
                        <div key={s.id}
                          className={`shift-block ${colorFor(s.employee_id)}`}
                          onClick={ev => { ev.stopPropagation(); if (s.shift_date >= todayISO) setConfirmDel(s); }}
                          style={{ ...(dept ? { borderLeft:`3px solid ${dept.color}` } : {}), ...(s.shift_date < todayISO ? { cursor:'default', opacity:0.6, pointerEvents:'none' } : {}) }}
                        >
                          <div className="sb-name">{s.employee?.name?.split(' ')[0]}</div>
                          <div className="sb-pos">{s.position || s.department}</div>
                          <div className="sb-time">{fmtTime(s.start_time)}–{fmtTime(s.end_time)}</div>
                        </div>
                      );
                    })
                  }
                  {!isPastWeek && <div className="day-add-hint">+ Add shift</div>}
                </div>
              );
            })}
          </div>
        </div>
      </Card>
      <Card style={{marginBottom:16}}>
        <div className="card-header"><div className="card-title">Shifts this week ({displayedShifts.length})</div></div>
        <div className="table-wrapper">
          <table className="table">
            <thead><tr><th>Employee</th><th>Position</th><th>Date</th><th>Start</th><th>End</th><th>Dept</th><th>Hrs</th><th></th></tr></thead>
            <tbody>
              {displayedShifts.map(s => (
                <tr key={s.id}>
                  <td><div style={{display:'flex',alignItems:'center',gap:8}}><Avatar name={s.employee?.name} color={s.employee?.avatar_color} textColor={s.employee?.avatar_text_color} size="sm" /><span style={{fontWeight:500}}>{s.employee?.name}</span></div></td>
                  <td>{s.position}</td><td>{fmtDate(s.shift_date)}</td>
                  <td>{fmtTime(s.start_time)}</td><td>{fmtTime(s.end_time)}</td>
                  <td>{s.department}</td><td>{s.duration_hours}h</td>
                  <td>{s.shift_date >= todayISO && <Btn size="sm" variant="danger" onClick={() => setConfirmDel(s)}>Remove</Btn>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {displayedShifts.length === 0 && <div className="table-empty">No shifts this week. Click a day or use "+ Add shift".</div>}
        </div>
      </Card>

      {/* Add modal */}
      <Modal isOpen={addModal} onClose={() => setAddModal(false)} title={prefillDate ? `Add shift — ${fmtDate(prefillDate)}` : 'Add shift'}>
        <form onSubmit={handleAdd}>
          <FormRow label="Employee">
            <Select value={form.employeeId} onChange={e => setForm(f=>({...f,employeeId:e.target.value}))} required>
              <option value="">Select employee…</option>
              {employees.map(e => <option key={e.id} value={e.id}>{e.name} ({e.position})</option>)}
            </Select>
          </FormRow>
          <FormRow label="Date"><Input type="date" value={form.date} onChange={e=>setForm(f=>({...f,date:e.target.value}))} required /></FormRow>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <FormRow label="Start time"><Input type="time" step="900" value={form.startTime} onChange={e=>setForm(f=>({...f,startTime:e.target.value}))} required /></FormRow>
            <FormRow label="End time">  <Input type="time" step="900" value={form.endTime}   onChange={e=>setForm(f=>({...f,endTime:e.target.value}))}   required /></FormRow>
          </div>
          {availWarn && <div style={{background:'#fef9c3',border:'1px solid #fde047',borderRadius:'var(--radius-sm)',padding:'8px 12px',fontSize:13,color:'#854d0e',marginBottom:12}}>⚠ {availWarn}</div>}
          {overtimeWarn && <div style={{background:'#fef3e2',border:'1px solid #fed7aa',borderRadius:'var(--radius-sm)',padding:'8px 12px',fontSize:13,color:'#9a3412',marginBottom:12}}>⚠ {overtimeWarn}</div>}
          <FormRow label="Position"><Input value={form.position} onChange={e=>setForm(f=>({...f,position:e.target.value}))} placeholder="e.g. Cashier" /></FormRow>
          <FormRow label="Section">
            {departments.length > 0
              ? <Select value={form.departmentId} onChange={e=>{const dept=departments.find(d=>d.id===e.target.value);setForm(f=>({...f,departmentId:e.target.value,department:dept?.name||''}));}}>
                  <option value="">No section</option>
                  {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </Select>
              : <Input value={form.department} onChange={e=>setForm(f=>({...f,department:e.target.value}))} placeholder="e.g. Front End" />
            }
          </FormRow>
          <div className="modal-actions">
            <Btn onClick={() => setAddModal(false)}>Cancel</Btn>
            <Btn type="submit" variant="primary" disabled={saving}>{saving ? <Spinner/> : 'Add shift'}</Btn>
          </div>
        </form>
      </Modal>

      {/* Remove confirm */}
      <Modal isOpen={clearConfirm} onClose={() => setClearConfirm(false)} title="Clear all shifts?">
        <p style={{fontSize:14,color:'var(--muted)',marginBottom:8}}>This will remove all upcoming shifts for this week. Shifts that have already happened will not be affected.</p>
        <p style={{fontSize:14,fontWeight:600,color:'var(--text)',marginBottom:20}}>This cannot be undone.</p>
        <div className="modal-actions">
          <Btn onClick={() => setClearConfirm(false)}>Cancel</Btn>
          <Btn onClick={handleClearConfirmed} style={{ background:'#e03131', color:'#fff', borderColor:'#e03131' }}>Clear all</Btn>
        </div>
      </Modal>

      <Modal isOpen={!!confirmDel} onClose={() => setConfirmDel(null)} title="Remove shift?">
        {confirmDel && (
          <>
            <div className="modal-info-box"><strong>{confirmDel.position}</strong><div style={{color:'var(--muted)',fontSize:13,marginTop:4}}>{fmtDate(confirmDel.shift_date)} · {fmtTime(confirmDel.start_time)}–{fmtTime(confirmDel.end_time)}</div></div>
            <p style={{fontSize:13.5,color:'var(--muted)'}}>This will cancel any pending coverage requests for this shift.</p>
            <div className="modal-actions">
              <Btn onClick={() => setConfirmDel(null)}>Cancel</Btn>
              <Btn variant="danger" onClick={handleRemove}>Remove shift</Btn>
            </div>
          </>
        )}
      </Modal>

      {/* No team needs set */}
      <Modal isOpen={noNeedsModal} onClose={() => setNoNeedsModal(false)} title="Team needs not configured">
        <p style={{fontSize:13.5,color:'var(--muted)',marginBottom:20}}>Set up your team's position requirements before auto-generating. This only takes a minute and is saved for future use.</p>
        <div className="modal-actions">
          <Btn onClick={() => setNoNeedsModal(false)}>Cancel</Btn>
          <Btn variant="primary" onClick={() => { setNoNeedsModal(false); navigate('/manager/team-needs'); }}>Set up Team Needs</Btn>
        </div>
      </Modal>

      {/* Auto-generate confirmation */}
      <Modal isOpen={confirmGenModal} onClose={() => setConfirmGenModal(false)} title="Auto-generate schedule">
        <p style={{fontSize:13.5,color:'var(--muted)',marginBottom:12}}>Generate shifts for <strong>{fmtWeekRange(weekStart)}</strong> using your saved team needs:</p>
        <ul style={{fontSize:13,color:'var(--text)',marginBottom:20,paddingLeft:16,display:'flex',flexDirection:'column',gap:4}}>
          {tSlots.filter(s=>s.position?.trim()||s.department_id).map((s,i) => (
            <li key={i}>
              {s.departmentName || s.position || 'Slot'} — {s.count} person{s.count>1?'s':''}, {fmtTime(s.startTime+':00')} – {fmtTime(s.endTime+':00')}
            </li>
          ))}
        </ul>
        <div className="modal-actions">
          <Btn onClick={() => setConfirmGenModal(false)}>Cancel</Btn>
          <Btn variant="primary" onClick={handleAutoGenerate} disabled={generating}>{generating ? <Spinner /> : 'Generate shifts'}</Btn>
        </div>
      </Modal>
    </div>
  );
}

// ── Shift Requests ────────────────────────────────────────────────────────────
export function ManagerRequests() {
  const { org, profile, isOwner } = useAuth();
  const { requests, reload, loading } = useShiftRequests();
  const { toast, show } = useToast();
  const [teamFilter, setTeamFilter] = useState('');
  const [managers, setManagers]     = useState([]);

  useEffect(() => {
    if (!isOwner || !org) return;
    supabase.from('profiles').select('id,name').eq('org_id', org.id)
      .in('role', ['manager','owner']).eq('is_active', true).order('name')
      .then(({ data }) => setManagers(data || []));
  }, [isOwner, org]);

  async function handleReview(requestId, decision) {
    const req = requests.find(r => r.id === requestId);
    const shift = req?.available_shift?.shift;
    await supabase.from('shift_requests').update({ status: decision, reviewed_by: profile.id, reviewed_at: new Date().toISOString() }).eq('id', requestId);
    if (decision === 'approved' && shift) {
      const originalOwnerId = shift.employee_id;
      await supabase.from('shifts').update({ employee_id: req.requester_id }).eq('id', shift.id);
      await supabase.from('available_shifts').update({ is_open: false }).eq('id', req.available_shift_id);
      if (req.offered_shift_id) {
        // Trade: give the offered shift to the original shift owner
        await supabase.from('shifts').update({ employee_id: originalOwnerId }).eq('id', req.offered_shift_id);
        if (originalOwnerId) await supabase.from('notifications').insert({ org_id: org.id, user_id: originalOwnerId, text: `Your ${shift?.position} shift on ${shift?.shift_date} was traded with ${req.requester?.name}.` });
      }
    }
    await supabase.from('notifications').insert({ org_id: org.id, user_id: req.requester_id, text: `Your ${req.offered_shift_id ? 'trade' : 'swap'} request for ${shift?.position} on ${shift?.shift_date} was ${decision}.` });
    show(`Request ${decision}!`, decision === 'approved' ? 'success' : 'error');
    reload();
  }

  const displayed = teamFilter
    ? requests.filter(r => r.requester?.reports_to === teamFilter)
    : requests;

  if (loading) return <LoadingScreen />;
  return (
    <div className="page">
      <Toast toast={toast} />
      <PageHeader title="Shift requests" subtitle="Approve or deny employee shift swap requests" />
      {isOwner && managers.length > 1 && (
        <div style={{ marginBottom:14, display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontSize:13, color:'var(--muted)', fontWeight:500 }}>Team:</span>
          <Select value={teamFilter} onChange={e => setTeamFilter(e.target.value)} style={{ width:'auto', minWidth:180 }}>
            <option value="">All teams</option>
            {managers.map(m => <option key={m.id} value={m.id}>{m.name}'s team</option>)}
          </Select>
        </div>
      )}
      <Card>
        <div className="table-wrapper">
          <table className="table">
            <thead><tr><th>Requester</th><th>Type</th><th>Shift requested</th><th>Date</th><th>Status</th><th>Action</th></tr></thead>
            <tbody>
              {displayed.map(r => {
                const shift = r.available_shift?.shift;
                const isTrade = !!r.offered_shift_id;
                return (
                  <tr key={r.id}>
                    <td><div style={{display:'flex',alignItems:'center',gap:8}}><Avatar name={r.requester?.name} color={r.requester?.avatar_color} textColor={r.requester?.avatar_text_color} size="sm"/><span style={{fontWeight:500}}>{r.requester?.name}</span></div></td>
                    <td><span style={{fontSize:11,fontWeight:700,padding:'2px 7px',borderRadius:20,background: isTrade ? 'var(--amber-light,#fff8e1)' : 'var(--bg)',border:`1px solid ${isTrade ? 'var(--amber,#f59e0b)' : 'var(--border)'}`,color: isTrade ? 'var(--amber-dark,#92400e)' : 'var(--muted)'}}>{isTrade ? 'Trade' : 'Swap'}</span></td>
                    <td>
                      <div style={{fontWeight:500}}>{shift?.position}</div>
                      <div style={{fontSize:11,color:'var(--muted)'}}>{fmtTime(shift?.start_time)}–{fmtTime(shift?.end_time)}</div>
                      {isTrade && r.offered_shift && (
                        <div style={{fontSize:11,color:'var(--muted)',marginTop:3}}>
                          ⇄ offers: {r.offered_shift.position} {fmtTime(r.offered_shift.start_time)}–{fmtTime(r.offered_shift.end_time)}
                        </div>
                      )}
                    </td>
                    <td>{fmtDate(shift?.shift_date)}</td>
                    <td><Badge status={r.status} /></td>
                    <td>{r.status==='pending'
                      ? <div style={{display:'flex',gap:6}}><Btn size="sm" variant="success" onClick={()=>handleReview(r.id,'approved')}>Approve</Btn><Btn size="sm" variant="danger" onClick={()=>handleReview(r.id,'denied')}>Deny</Btn></div>
                      : <span style={{fontSize:12,color:'var(--muted)'}}>{r.status==='expired'?'⏰ Expired':'Reviewed'}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {displayed.length===0 && <div className="table-empty">No requests{teamFilter?' for this team':' yet'}.</div>}
        </div>
      </Card>
    </div>
  );
}

// ── Team Needs ────────────────────────────────────────────────────────────────
export function TeamNeeds() {
  const { org, profile, isOwner } = useAuth();
  const { toast, show } = useToast();
  const { departments } = useDepartments();
  const deptOptions = departments.filter(d => !d.is_any);
  const [slots, setSlots] = useState([]);
  const [days, setDays]   = useState([1,2,3,4,5]);
  const [saving, setSaving] = useState(false);
  const [managers, setManagers] = useState([]);
  const [selectedManagerId, setSelectedManagerId] = useState(null);
  const [loadedForId, setLoadedForId] = useState(null);
  const [positionOptions, setPositionOptions] = useState([]);

  useEffect(() => {
    if (profile && !selectedManagerId) setSelectedManagerId(profile.id);
  }, [profile, selectedManagerId]);

  useEffect(() => {
    if (!isOwner || !org) return;
    supabase.from('profiles').select('id,name,role')
      .eq('org_id', org.id).in('role', ['manager','owner']).eq('is_active', true).order('name')
      .then(({ data }) => setManagers(data || []));
  }, [isOwner, org]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!org || !selectedManagerId) return;
    if (loadedForId === selectedManagerId) return;
    supabase.from('schedule_templates').select('*')
      .eq('org_id', org.id).eq('manager_id', selectedManagerId).maybeSingle()
      .then(({ data }) => {
        if (data) { setSlots(data.slots || []); setDays(data.days || [1,2,3,4,5]); }
        else { setSlots([]); setDays([1,2,3,4,5]); }
        setLoadedForId(selectedManagerId);
      });
  }, [org, selectedManagerId, loadedForId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!org || !selectedManagerId) return;
    supabase.from('profiles').select('position')
      .eq('org_id', org.id).eq('reports_to', selectedManagerId).eq('is_active', true)
      .then(({ data }) => {
        const unique = [...new Set((data || []).map(p => p.position).filter(Boolean))].sort();
        setPositionOptions(unique);
      });
  }, [org, selectedManagerId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSave(e) {
    e.preventDefault();
    if (!selectedManagerId) return;
    setSaving(true);
    try {
      await supabase.from('schedule_templates').upsert({
        org_id: org.id, manager_id: selectedManagerId,
        slots, days, updated_at: new Date().toISOString(),
      }, { onConflict: 'org_id,manager_id' });
      show('Team needs saved!');
    } catch (err) { show(err.message, 'error'); }
    finally { setSaving(false); }
  }

  if (!loadedForId) return <LoadingScreen />;

  return (
    <div className="page">
      <Toast toast={toast} />
      <PageHeader
        title="Team needs"
        subtitle="Set staffing requirements per slot. Auto-generate uses these to fill your schedule."
      />

      {/* Owner: choose whose needs to edit */}
      {isOwner && managers.length > 1 && (
        <div style={{marginBottom:20,display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
          <span style={{fontSize:13.5,fontWeight:500,color:'var(--muted)',flexShrink:0}}>Editing needs for:</span>
          <Select
            value={selectedManagerId || ''}
            onChange={e => { setSelectedManagerId(e.target.value); setLoadedForId(null); }}
            style={{width:'auto',minWidth:200}}
          >
            {managers.map(m => (
              <option key={m.id} value={m.id}>
                {m.name}{m.id === profile?.id ? ' (you)' : ''}{m.role === 'owner' ? ' · Owner' : ''}
              </option>
            ))}
          </Select>
        </div>
      )}

      <Card>
        <form onSubmit={handleSave}>

          {/* Slot list */}
          <div style={{marginBottom:24}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
              <div style={{fontSize:12,color:'var(--muted)',fontWeight:600,textTransform:'uppercase',letterSpacing:0.5}}>Staffing slots</div>
              <Btn size="sm" type="button" onClick={() => setSlots(prev => [...prev, {position:'',department_id:null,departmentName:'',count:1,startTime:'09:00',endTime:'17:00'}])}>+ Add slot</Btn>
            </div>

            {slots.length === 0 && (
              <div style={{textAlign:'center',padding:'24px 16px',color:'var(--muted)',fontSize:13.5,border:'1px dashed var(--border-md)',borderRadius:'var(--radius-sm)',background:'var(--bg)'}}>
                No slots yet — add one to tell auto-generate how many people you need and when.
              </div>
            )}

            {slots.map((slot, i) => {
              const selectedDept = departments.find(d => d.id === slot.department_id);
              return (
                <div key={i} style={{background:'var(--bg)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)',padding:'14px 16px',marginBottom:8}}>
                  <div style={{display:'flex',gap:10,flexWrap:'wrap',alignItems:'flex-end'}}>

                    {/* Section or position */}
                    <div style={{flex:'1 1 150px',minWidth:130}}>
                      <div style={{fontSize:11,color:'var(--muted)',fontWeight:600,marginBottom:5,textTransform:'uppercase',letterSpacing:0.4}}>
                        {deptOptions.length > 0 ? 'Section' : 'Position'}
                      </div>
                      {deptOptions.length > 0 ? (
                        <Select
                          value={slot.department_id || ''}
                          onChange={e => {
                            const dept = departments.find(d => d.id === e.target.value);
                            setSlots(prev => prev.map((s,j) => j===i
                              ? { ...s, department_id: e.target.value || null, departmentName: dept?.name || '' }
                              : s));
                          }}
                          style={{width:'100%'}}
                        >
                          <option value="">Any section</option>
                          {deptOptions.map(d => (
                            <option key={d.id} value={d.id}>{d.name}</option>
                          ))}
                        </Select>
                      ) : (
                        <Select
                          value={slot.position || ''}
                          onChange={e => setSlots(prev => prev.map((s,j) => j===i ? {...s,position:e.target.value} : s))}
                          style={{width:'100%'}}
                        >
                          <option value="">Select position…</option>
                          {positionOptions.map(p => <option key={p} value={p}>{p}</option>)}
                        </Select>
                      )}
                    </div>

                    {/* Role label — only when sections exist */}
                    {deptOptions.length > 0 && (
                      <div style={{flex:'1 1 120px',minWidth:100}}>
                        <div style={{fontSize:11,color:'var(--muted)',fontWeight:600,marginBottom:5,textTransform:'uppercase',letterSpacing:0.4}}>Position</div>
                        <Select
                          value={slot.position || ''}
                          onChange={e => setSlots(prev => prev.map((s,j) => j===i ? {...s,position:e.target.value} : s))}
                          style={{width:'100%'}}
                        >
                          <option value="">Any position</option>
                          {positionOptions.map(p => <option key={p} value={p}>{p}</option>)}
                        </Select>
                      </div>
                    )}

                    {/* Headcount */}
                    <div style={{flex:'0 0 68px'}}>
                      <div style={{fontSize:11,color:'var(--muted)',fontWeight:600,marginBottom:5,textTransform:'uppercase',letterSpacing:0.4}}>People</div>
                      <Input type="number" min={1} max={50} value={slot.count}
                        onChange={e => setSlots(prev => prev.map((s,j) => j===i ? {...s,count:+e.target.value} : s))}
                        style={{width:'100%',textAlign:'center'}}
                      />
                    </div>

                    {/* Shift time */}
                    <div style={{flex:'0 0 auto',display:'flex',alignItems:'flex-end',gap:6}}>
                      <div>
                        <div style={{fontSize:11,color:'var(--muted)',fontWeight:600,marginBottom:5,textTransform:'uppercase',letterSpacing:0.4}}>Start</div>
                        <Input type="time" step="900" value={slot.startTime}
                          onChange={e => setSlots(prev => prev.map((s,j) => j===i ? {...s,startTime:e.target.value} : s))}
                          style={{width:106}}
                        />
                      </div>
                      <span style={{color:'var(--muted)',paddingBottom:9,fontSize:13}}>→</span>
                      <div>
                        <div style={{fontSize:11,color:'var(--muted)',fontWeight:600,marginBottom:5,textTransform:'uppercase',letterSpacing:0.4}}>End</div>
                        <Input type="time" step="900" value={slot.endTime}
                          onChange={e => setSlots(prev => prev.map((s,j) => j===i ? {...s,endTime:e.target.value} : s))}
                          style={{width:106}}
                        />
                      </div>
                    </div>

                    {/* Remove */}
                    <button
                      type="button"
                      onClick={() => setSlots(prev => prev.filter((_,j) => j!==i))}
                      style={{background:'none',border:'1px solid var(--border-md)',borderRadius:'var(--radius-sm)',cursor:'pointer',color:'var(--muted)',fontSize:13,padding:'0 9px',height:34,lineHeight:'34px',flexShrink:0,transition:'all 0.12s'}}
                      title="Remove slot"
                      onMouseEnter={e=>{e.currentTarget.style.background='var(--red-light)';e.currentTarget.style.color='var(--red)';e.currentTarget.style.borderColor='var(--red)';}}
                      onMouseLeave={e=>{e.currentTarget.style.background='none';e.currentTarget.style.color='var(--muted)';e.currentTarget.style.borderColor='var(--border-md)';}}
                    >✕</button>
                  </div>

                  {selectedDept && (
                    <div style={{marginTop:8,display:'flex',alignItems:'center',gap:6,fontSize:12,color:'var(--muted)'}}>
                      <div style={{width:8,height:8,borderRadius:'50%',background:selectedDept.color,flexShrink:0}} />
                      <span>{selectedDept.name}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Working days */}
          <div style={{marginBottom:24}}>
            <div style={{fontSize:12,color:'var(--muted)',fontWeight:600,marginBottom:12,textTransform:'uppercase',letterSpacing:0.5}}>Working days</div>
            <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
              {[{label:'Mon',dow:1},{label:'Tue',dow:2},{label:'Wed',dow:3},{label:'Thu',dow:4},{label:'Fri',dow:5},{label:'Sat',dow:6},{label:'Sun',dow:0}].map(({label,dow}) => {
                const on = days.includes(dow);
                return (
                  <button
                    key={dow} type="button"
                    onClick={() => setDays(prev => on ? prev.filter(d=>d!==dow) : [...prev,dow])}
                    style={{
                      padding:'7px 16px',borderRadius:20,cursor:'pointer',fontSize:13,fontWeight: on ? 600 : 400,
                      border:`2px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
                      background: on ? 'var(--accent-light)' : 'transparent',
                      color: on ? 'var(--accent)' : 'var(--muted)',
                      transition:'all 0.12s',
                    }}
                  >{label}</button>
                );
              })}
            </div>
          </div>

          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:10}}>
            <span style={{fontSize:12.5,color:'var(--muted)'}}>
              {slots.length} slot{slots.length !== 1 ? 's' : ''} · {days.length} day{days.length !== 1 ? 's' : ''} per week
            </span>
            <Btn type="submit" variant="primary" disabled={saving}>{saving ? <Spinner /> : 'Save team needs'}</Btn>
          </div>

        </form>
      </Card>
    </div>
  );
}

// ── Employees ─────────────────────────────────────────────────────────────────
export function ManagerEmployees() {
  const { reports, loading, reload } = useDirectReports();
  const { org, profile, isOwner } = useAuth();
  const directManagers = reports.filter(r => r.role === 'manager');
  const employees      = reports.filter(r => r.role === 'employee');
  const { invites, sendInvite, revokeInvite } = useInvites();
  const { departments } = useDepartments();
  const { toast, show } = useToast();
  const [inviteModal, setInviteModal] = useState(false);
  const [confirmDel, setConfirmDel]   = useState(null);
  const [availEmp, setAvailEmp]       = useState(null);
  const [sending, setSending]         = useState(false);
  const [inviteLink, setInviteLink]   = useState('');
  const [form, setForm] = useState({ name:'', email:'', position:'', departmentIds:[], role:'employee', reportsTo:'' });
  const [allManagers, setAllManagers] = useState([]);
  // Owner-only: all org members + collapsible + edit
  const [allOrgMembers, setAllOrgMembers] = useState([]);
  const [orgLoading, setOrgLoading]       = useState(false);
  const [collapsed, setCollapsed]         = useState({});
  const [editEmp, setEditEmp]             = useState(null);
  const [editForm, setEditForm]           = useState({ position:'', departmentIds:[], minWeeklyShifts:0 });
  const [editSaving, setEditSaving]       = useState(false);
  const [editEmpDepts, setEditEmpDepts]   = useState([]);

  useEffect(() => {
    if (!isOwner || !org) return;
    supabase.from('profiles').select('id,name,role').eq('org_id', org.id)
      .in('role', ['manager','owner']).eq('is_active', true).order('name')
      .then(({ data }) => setAllManagers(data || []));
  }, [isOwner, org]);

  function loadOrgMembers() {
    if (!isOwner || !org) return;
    setOrgLoading(true);
    supabase.from('profiles').select('*').eq('org_id', org.id).eq('is_active', true).order('name')
      .then(({ data }) => { setAllOrgMembers(data || []); setOrgLoading(false); });
  }
  useEffect(() => { loadOrgMembers(); }, [isOwner, org]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset form defaults when modal opens
  useEffect(() => {
    if (inviteModal) setForm(f => ({ ...f, reportsTo: profile?.id || '', departmentIds: [] }));
  }, [inviteModal, profile]);

  async function handleInvite(e) {
    e.preventDefault(); setSending(true);
    try {
      const payload = {
        name:           form.name,
        email:          form.email,
        position:       form.position,
        department_ids: form.departmentIds,
        role:           form.role,
        ...(isOwner && form.reportsTo && form.reportsTo !== profile.id
          ? { reports_to: form.reportsTo } : {}),
      };
      const invite = await sendInvite(payload);
      const link = `${process.env.REACT_APP_URL}/invite?token=${invite.token}`;
      setInviteLink(link);
      show(`Invite created for ${form.name}!`);
      setForm({ name:'', email:'', position:'', departmentIds:[], role:'employee', reportsTo: profile?.id || '' });
    } catch (err) { show(err.message, 'error'); }
    finally { setSending(false); }
  }

  async function handleRemove() {
    if (!confirmDel) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/delete-employee`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ employeeId: confirmDel.id }),
      });
      if (!res.ok) throw new Error(await res.text());
      show(`${confirmDel.name} has been removed.`);
      setConfirmDel(null);
      reload(); loadOrgMembers();
    } catch (err) {
      show(err.message || 'Failed to remove employee.', 'error');
    }
  }

  function openEdit(emp) {
    setEditEmp(emp);
    setEditForm({ position: emp.position || '', departmentIds: [], minWeeklyShifts: emp.min_weekly_shifts ?? 0 });
    // Load current department assignments
    supabase.from('employee_departments').select('department_id')
      .eq('employee_id', emp.id)
      .then(({ data }) => setEditEmpDepts((data || []).map(d => d.department_id)));
  }

  function toggleEditDept(id) {
    setEditEmpDepts(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  }

  async function handleEditSave(e) {
    e.preventDefault();
    setEditSaving(true);
    try {
      await supabase.from('profiles').update({ position: editForm.position, min_weekly_shifts: editForm.minWeeklyShifts }).eq('id', editEmp.id);
      // Replace all department assignments
      await supabase.from('employee_departments').delete().eq('employee_id', editEmp.id);
      if (editEmpDepts.length > 0) {
        const rows = editEmpDepts.map(dept_id => ({
          employee_id: editEmp.id, department_id: dept_id, org_id: org.id,
        }));
        await supabase.from('employee_departments').insert(rows);
      }
      show('Updated!');
      setEditEmp(null);
      reload(); loadOrgMembers();
    } catch (err) { show(err.message, 'error'); }
    finally { setEditSaving(false); }
  }

  // Current-week shift counts
  const weekStart = toISO(getWeekStart(new Date()));
  const weekEnd   = toISO(addDays(getWeekStart(new Date()),6));
  const { shifts } = useShifts(weekStart, weekEnd);
  const shiftCounts = {};
  shifts.forEach(s => { shiftCounts[s.employee_id] = (shiftCounts[s.employee_id]||0)+1; });

  // Employee → departments map for badge display
  const [orgEmpDeptMap, setOrgEmpDeptMap] = useState({});
  useEffect(() => {
    if (!org) return;
    supabase.from('employee_departments')
      .select('employee_id, department_id, departments(id, name, color, is_any)')
      .eq('org_id', org.id)
      .then(({ data }) => {
        const map = {};
        (data || []).forEach(ed => {
          if (!map[ed.employee_id]) map[ed.employee_id] = [];
          map[ed.employee_id].push(ed);
        });
        setOrgEmpDeptMap(map);
      });
  }, [org, allOrgMembers]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || (isOwner && orgLoading)) return <LoadingScreen />;

  // Owner: build per-manager sections from all org members
  const managerSections = isOwner ? (() => {
    const myDirects = allOrgMembers.filter(p => p.reports_to === profile.id);
    const mgrs = allOrgMembers.filter(p => p.role === 'manager');
    return { myDirects, mgrs };
  })() : null;

  function EmpCard({ e, showEdit, empDeptMap }) {
    const count = shiftCounts[e.id] || 0;
    const empDepts = empDeptMap ? (empDeptMap[e.id] || []) : [];
    const statusColors = { active:'var(--green)', inactive:'var(--muted)', seasonal:'var(--amber)', leave:'var(--amber)' };
    const st = e.status || 'active';
    return (
      <div className="emp-card" style={{ opacity: st !== 'active' ? 0.75 : 1 }}>
        <Avatar name={e.name} color={e.avatar_color} textColor={e.avatar_text_color} size="lg" />
        <div className="emp-info">
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            <div className="emp-name">{e.name}</div>
            {st !== 'active' && (
              <span style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', color: statusColors[st], letterSpacing:0.4 }}>{st}</span>
            )}
          </div>
          <div className="emp-sub">{e.position}</div>
          {empDepts.length > 0 && (
            <div style={{ display:'flex', gap:4, flexWrap:'wrap', marginTop:4 }}>
              {empDepts.map(d => <DepartmentBadge key={d.department_id} name={d.departments?.name} color={d.departments?.color} />)}
            </div>
          )}
          <div className="emp-email">{e.email}</div>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:4 }}>
            <div style={{fontSize:12,color:count>0?'var(--green)':'var(--hint)',fontWeight:500}}>
              {count>0?`${count} shift${count>1?'s':''} this week`:'No shifts this week'}
            </div>
            {(e.scheduling_priority ?? 50) !== 50 && (
              <span style={{ fontSize:11, color:'var(--muted)' }}>P{e.scheduling_priority}</span>
            )}
            {(e.min_weekly_shifts ?? 0) > 0 && (
              <span style={{ fontSize:11, color:'var(--muted)' }}>min {e.min_weekly_shifts}/wk</span>
            )}
          </div>
          <div style={{display:'flex',gap:6,marginTop:6,flexWrap:'wrap'}}>
            <Btn size="sm" variant="ghost" style={{padding:'2px 8px',fontSize:11}} onClick={() => setAvailEmp(e)}>Availability</Btn>
            {showEdit && <Btn size="sm" variant="ghost" style={{padding:'2px 8px',fontSize:11}} onClick={() => openEdit(e)}>Edit</Btn>}
            {showEdit && st === 'active' && (
              <Btn size="sm" variant="ghost" style={{padding:'2px 8px',fontSize:11,color:'var(--amber)'}}
                onClick={async () => {
                  await supabase.from('profiles').update({ status:'inactive' }).eq('id', e.id);
                  show(`${e.name} paused from scheduling.`); loadOrgMembers();
                }}>Pause</Btn>
            )}
            {showEdit && st !== 'active' && (
              <Btn size="sm" variant="ghost" style={{padding:'2px 8px',fontSize:11,color:'var(--green)'}}
                onClick={async () => {
                  await supabase.from('profiles').update({ status:'active', is_active:true }).eq('id', e.id);
                  show(`${e.name} reactivated.`); loadOrgMembers();
                }}>Reactivate</Btn>
            )}
          </div>
        </div>
        <button className="emp-remove-btn" onClick={() => setConfirmDel(e)} title="Remove">✕</button>
      </div>
    );
  }

  return (
    <div className="page">
      <Toast toast={toast} />
      <PageHeader
        title="Employees"
        subtitle={isOwner ? `${allOrgMembers.filter(p=>p.role==='employee').length} employees across your organization` : `${reports.length} direct report${reports.length!==1?'s':''}`}
        action={<Btn variant="primary" size="sm" onClick={() => { setInviteModal(true); setInviteLink(''); }}>+ Invite</Btn>}
      />

      {/* Pending invites */}
      {invites.length > 0 && (
        <Card style={{marginBottom:16}}>
          <div className="card-header"><div className="card-title">Pending invites</div></div>
          {invites.map(inv => (
            <div key={inv.id} className="invite-pill">
              <div className="invite-pill-info">
                <div className="invite-pill-name">{inv.name}</div>
                <div className="invite-pill-email">{inv.email} · {inv.position}</div>
              </div>
              <Badge status="pending" />
              <Btn size="sm" variant="ghost" onClick={async () => {
                try { await revokeInvite(inv.id); show('Invite revoked.'); }
                catch (err) { show(err.message || 'Failed to revoke invite.', 'error'); }
              }}>Revoke</Btn>
            </div>
          ))}
        </Card>
      )}

      {isOwner ? (
        <>
          {/* Owner view: Direct + per-manager sections */}
          <Card style={{marginBottom:16}}>
            <div className="card-header"><div className="card-title">Direct ({managerSections.myDirects.length})</div></div>
            {managerSections.myDirects.length === 0
              ? <p style={{fontSize:13,color:'var(--muted)',padding:'8px 0'}}>No one reports directly to you.</p>
              : <div className="employees-grid">{managerSections.myDirects.map(e => <EmpCard key={e.id} e={e} showEdit empDeptMap={orgEmpDeptMap} />)}</div>
            }
          </Card>
          {managerSections.mgrs.map(mgr => {
            const team = allOrgMembers.filter(p => p.reports_to === mgr.id);
            const isCollapsed = collapsed[mgr.id];
            return (
              <Card key={mgr.id} style={{marginBottom:12}}>
                <div className="card-header" style={{cursor:'pointer'}} onClick={() => setCollapsed(c=>({...c,[mgr.id]:!c[mgr.id]}))}>
                  <div className="card-title">{mgr.name}'s team ({team.length})</div>
                  <span style={{fontSize:12,color:'var(--muted)'}}>{isCollapsed?'▸':'▾'}</span>
                </div>
                {!isCollapsed && (
                  team.length === 0
                    ? <p style={{fontSize:13,color:'var(--muted)',padding:'8px 0'}}>No employees on this team yet.</p>
                    : <div className="employees-grid">{team.map(e => <EmpCard key={e.id} e={e} showEdit empDeptMap={orgEmpDeptMap} />)}</div>
                )}
              </Card>
            );
          })}
        </>
      ) : (
        <>
          {/* Manager view: existing layout */}
          {directManagers.length > 0 && (
            <Card style={{marginBottom:16}}>
              <div className="card-header"><div className="card-title">Managers</div></div>
              <div className="employees-grid">
                {directManagers.map(m => (
                  <div key={m.id} className="emp-card">
                    <Avatar name={m.name} color={m.avatar_color} textColor={m.avatar_text_color} size="lg" />
                    <div className="emp-info">
                      <div className="emp-name">{m.name}</div>
                      <div className="emp-sub">{m.position} · {m.department}</div>
                      <div className="emp-email">{m.email}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
          <div className="employees-grid">
            {employees.map(e => {
              const count = shiftCounts[e.id] || 0;
              return (
                <div key={e.id} className="emp-card">
                  <Avatar name={e.name} color={e.avatar_color} textColor={e.avatar_text_color} size="lg" />
                  <div className="emp-info">
                    <div className="emp-name">{e.name}</div>
                    <div className="emp-sub">{e.position} · {e.department}</div>
                    <div className="emp-email">{e.email}</div>
                    <div style={{fontSize:12,color:count>0?'var(--green)':'var(--hint)',marginTop:4,fontWeight:500}}>
                      {count>0?`${count} shift${count>1?'s':''} this week`:'No shifts this week'}
                    </div>
                    <Btn size="sm" variant="ghost" style={{marginTop:6,padding:'2px 8px',fontSize:11}} onClick={() => setAvailEmp(e)}>View availability</Btn>
                  </div>
                  <button className="emp-remove-btn" onClick={() => setConfirmDel(e)} title="Remove">✕</button>
                </div>
              );
            })}
          </div>
          {employees.length===0 && <EmptyState icon="👥" title="No employees yet" message="Invite your first team member." action={<Btn variant="primary" onClick={()=>setInviteModal(true)}>+ Invite employee</Btn>} />}
        </>
      )}

      {/* Invite modal */}
      <Modal isOpen={inviteModal} onClose={() => setInviteModal(false)} title="Invite employee">
        {inviteLink
          ? <div>
              <p style={{fontSize:13.5,marginBottom:12}}>Share this invite link with <strong>{form.name || 'your employee'}</strong>. It expires in 7 days.</p>
              <div style={{background:'var(--bg)',borderRadius:'var(--radius-sm)',padding:'12px',fontFamily:'monospace',fontSize:12,wordBreak:'break-all',marginBottom:16}}>{inviteLink}</div>
              <Btn full onClick={() => {navigator.clipboard.writeText(inviteLink); show('Link copied!');}}>📋 Copy link</Btn>
              <div className="modal-actions"><Btn onClick={() => setInviteModal(false)}>Done</Btn></div>
            </div>
          : <form onSubmit={handleInvite}>
              <FormRow label="Full name"><Input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="Jane Smith" required /></FormRow>
              <FormRow label="Email" hint="They'll use this to log in"><Input type="email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} placeholder="jane@company.com" required /></FormRow>
              <FormRow label="Position"><Input value={form.position} onChange={e=>setForm(f=>({...f,position:e.target.value}))} placeholder="e.g. Cashier, Server, Aide" /></FormRow>
              {departments.filter(d => !d.is_any).length > 0 && (
                <FormRow label="Sections" hint="Which sections can this person work?">
                  <div style={{display:'flex',gap:6,flexWrap:'wrap',paddingTop:2}}>
                    {departments.filter(d => !d.is_any).map(d => {
                      const sel = form.departmentIds.includes(d.id);
                      return (
                        <button key={d.id} type="button"
                          onClick={() => setForm(f => ({
                            ...f,
                            departmentIds: sel
                              ? f.departmentIds.filter(x => x !== d.id)
                              : [...f.departmentIds, d.id],
                          }))}
                          style={{
                            padding:'5px 11px', borderRadius:20, cursor:'pointer', fontSize:12, fontWeight:500,
                            border: `2px solid ${sel ? d.color : 'var(--border)'}`,
                            background: sel ? d.color : 'transparent',
                            color: sel ? '#fff' : 'var(--text)',
                            transition: 'all 0.15s',
                          }}
                        >{d.name}</button>
                      );
                    })}
                    {(() => {
                      const anyDept = departments.find(d => d.is_any);
                      if (!anyDept) return null;
                      const sel = form.departmentIds.includes(anyDept.id);
                      return (
                        <button key={anyDept.id} type="button"
                          onClick={() => setForm(f => ({
                            ...f,
                            departmentIds: sel
                              ? f.departmentIds.filter(x => x !== anyDept.id)
                              : [...f.departmentIds, anyDept.id],
                          }))}
                          style={{
                            padding:'5px 11px', borderRadius:20, cursor:'pointer', fontSize:12, fontWeight:500,
                            border:`2px solid ${sel ? '#64748b' : 'var(--border)'}`,
                            background: sel ? '#64748b' : 'transparent',
                            color: sel ? '#fff' : 'var(--text)',
                            transition:'all 0.15s',
                          }}
                        >Any</button>
                      );
                    })()}
                  </div>
                </FormRow>
              )}
              <FormRow label="Role">
                <Select value={form.role} onChange={e=>setForm(f=>({...f,role:e.target.value}))}>
                  <option value="employee">Employee</option>
                  <option value="manager">Manager</option>
                </Select>
              </FormRow>
              {isOwner && allManagers.length > 1 && form.role !== 'owner' && (
                <FormRow label="Reports to" hint="Which manager will this person work under?">
                  <Select value={form.reportsTo} onChange={e=>setForm(f=>({...f,reportsTo:e.target.value}))}>
                    {allManagers.map(m => (
                      <option key={m.id} value={m.id}>{m.name}{m.id === profile?.id ? ' (you)' : ''}</option>
                    ))}
                  </Select>
                </FormRow>
              )}
              <div className="modal-actions">
                <Btn onClick={() => setInviteModal(false)}>Cancel</Btn>
                <Btn type="submit" variant="primary" disabled={sending}>{sending ? <Spinner/> : 'Generate invite link'}</Btn>
              </div>
            </form>
        }
      </Modal>

      {/* Remove confirm */}
      <Modal isOpen={!!confirmDel} onClose={() => setConfirmDel(null)} title="Remove employee?">
        {confirmDel && (
          <>
            <div style={{display:'flex',alignItems:'center',gap:12,background:'var(--bg)',borderRadius:'var(--radius-sm)',padding:14,marginBottom:16}}>
              <Avatar name={confirmDel.name} color={confirmDel.avatar_color} textColor={confirmDel.avatar_text_color} size="lg" />
              <div><div style={{fontWeight:600}}>{confirmDel.name}</div><div style={{fontSize:13,color:'var(--muted)'}}>{confirmDel.position} · {confirmDel.department}</div></div>
            </div>
            <p style={{fontSize:13.5,color:'var(--muted)'}}>This will remove <strong>{confirmDel.name}</strong> from your team. Their historical shifts will be preserved for records.</p>
            <div className="modal-actions">
              <Btn onClick={() => setConfirmDel(null)}>Cancel</Btn>
              <Btn variant="danger" onClick={handleRemove}>Remove member</Btn>
            </div>
          </>
        )}
      </Modal>

      {/* Employee availability */}
      <Modal isOpen={!!availEmp} onClose={() => setAvailEmp(null)} title={availEmp ? `${availEmp.name}'s availability` : 'Availability'}>
        {availEmp && <EmployeeAvailabilityView employeeId={availEmp.id} />}
      </Modal>

      {/* Edit position/departments (owner only) */}
      <Modal isOpen={!!editEmp} onClose={() => setEditEmp(null)} title={editEmp ? `Edit ${editEmp.name}` : 'Edit'}>
        {editEmp && (
          <form onSubmit={handleEditSave}>
            <FormRow label="Position">
              <Input value={editForm.position} onChange={e=>setEditForm(f=>({...f,position:e.target.value}))} placeholder="Cashier" />
            </FormRow>
            <FormRow label="Scheduling priority" hint="0 = lowest, 100 = highest (default 50)">
              <Input type="number" min={0} max={100}
                value={editEmp.scheduling_priority ?? 50}
                onChange={async e => {
                  const v = Number(e.target.value);
                  await supabase.from('profiles').update({ scheduling_priority: v }).eq('id', editEmp.id);
                  setEditEmp(p => ({ ...p, scheduling_priority: v }));
                }}
              />
            </FormRow>
            <FormRow label="Min shifts per week" hint="Guaranteed before extras go to higher-priority people. 0 = no minimum.">
              <Input type="number" min={0} max={14}
                value={editForm.minWeeklyShifts}
                onChange={e => setEditForm(f => ({ ...f, minWeeklyShifts: Number(e.target.value) }))}
              />
            </FormRow>
            {departments.filter(d => !d.is_any).length > 0 && (
              <FormRow label="Sections">
                <div style={{display:'flex',gap:6,flexWrap:'wrap',paddingTop:2}}>
                  {departments.map(d => {
                    const sel = editEmpDepts.includes(d.id);
                    return (
                      <button key={d.id} type="button"
                        onClick={() => toggleEditDept(d.id)}
                        style={{
                          padding:'5px 11px', borderRadius:20, cursor:'pointer', fontSize:12, fontWeight:500,
                          border:`2px solid ${sel ? (d.color || '#64748b') : 'var(--border)'}`,
                          background: sel ? (d.color || '#64748b') : 'transparent',
                          color: sel ? '#fff' : 'var(--text)',
                          transition:'all 0.15s',
                        }}
                      >{d.name}</button>
                    );
                  })}
                </div>
              </FormRow>
            )}
            <div className="modal-actions">
              <Btn onClick={() => setEditEmp(null)}>Cancel</Btn>
              <Btn type="submit" variant="primary" disabled={editSaving}>{editSaving ? <Spinner /> : 'Save'}</Btn>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}

// ── Org Hierarchy (owner only) ────────────────────────────────────────────────
function TreeNode({ profile, childrenOf, dragId, dropTargetId, onDragStart, onDragOver, onDrop, onDragEnd, onPromote, onSwap, isOwner }) {
  const children = childrenOf[profile.id] || [];
  const isManager = profile.role === 'manager' || profile.role === 'owner';
  // Only allow dropping onto managers/owners, not plain employees
  const isValidDropTarget = isManager && dropTargetId === profile.id && dragId && dragId !== profile.id;
  return (
    <div style={{ marginLeft: 0 }}>
      <div
        className="tree-node"
        draggable={isOwner && profile.role !== 'owner'}
        onDragStart={isOwner ? (e) => onDragStart(e, profile.id) : undefined}
        onDragOver={isOwner && isManager ? (e) => { e.preventDefault(); onDragOver(profile.id); } : undefined}
        onDrop={isOwner && isManager ? (e) => { e.preventDefault(); e.stopPropagation(); onDrop(profile.id); } : undefined}
        onDragEnd={isOwner ? onDragEnd : undefined}
        style={{
          cursor: isOwner && profile.role !== 'owner' ? 'grab' : 'default',
          border: isValidDropTarget ? '2px dashed var(--accent)' : '2px solid transparent',
          borderRadius: 'var(--radius-sm)',
          transition: 'border-color 0.15s',
          background: isValidDropTarget ? 'var(--accent-light,#f0f0ff)' : undefined,
        }}
      >
        <Avatar name={profile.name} color={profile.avatar_color} textColor={profile.avatar_text_color} size="md" />
        <div className="tree-node-info">
          <div className="tree-node-name">{profile.name}</div>
          <div className="tree-node-meta">
            {[profile.role, profile.position, profile.department].filter(Boolean).join(' · ')}
          </div>
        </div>
        {isOwner && profile.role !== 'owner' && (
          <div className="tree-node-actions">
            {profile.role === 'manager' && (
              <button
                onClick={(e) => { e.stopPropagation(); onSwap(profile); }}
                title="Swap this manager's team with another"
                style={{ fontSize:11, padding:'2px 8px', borderRadius:20, border:'1px solid var(--border)', background:'var(--bg)', color:'var(--muted)', cursor:'pointer', whiteSpace:'nowrap' }}
              >
                ⇄ Swap team
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); onPromote(profile); }}
              title={profile.role === 'employee' ? 'Promote to manager' : 'Demote to employee'}
              style={{ fontSize:11, padding:'2px 8px', borderRadius:20, border:'1px solid var(--border)', background:'var(--bg)', color:'var(--muted)', cursor:'pointer', whiteSpace:'nowrap' }}
            >
              {profile.role === 'employee' ? '↑ Make manager' : '↓ Make employee'}
            </button>
            <span style={{ fontSize:11, color:'var(--muted)', opacity:0.5, paddingRight:4 }}>⠿</span>
          </div>
        )}
      </div>
      {children.length > 0 && (
        <div style={{ marginLeft: 28, borderLeft:'2px solid var(--border)', paddingLeft:12, marginTop:2, marginBottom:2 }}>
          {children.map(c => (
            <TreeNode key={c.id} profile={c} childrenOf={childrenOf}
              dragId={dragId} dropTargetId={dropTargetId}
              onDragStart={onDragStart} onDragOver={onDragOver} onDrop={onDrop} onDragEnd={onDragEnd}
              onPromote={onPromote} onSwap={onSwap} isOwner={isOwner}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function OrgHierarchy() {
  const { isOwner, org } = useAuth();
  const navigate = useNavigate();
  const { toast, show } = useToast();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [dragId, setDragId]       = useState(null);
  const dragIdRef = React.useRef(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const [saving, setSaving]       = useState(false);
  const [promoteTarget, setPromoteTarget] = useState(null);
  const [swapTarget, setSwapTarget]       = useState(null);
  const [swapWith, setSwapWith]           = useState(null);

  useEffect(() => {
    if (!isOwner) navigate('/manager/dashboard', { replace: true });
  }, [isOwner, navigate]);

  function loadProfiles() {
    if (!org || !isOwner) return;
    supabase
      .from('profiles')
      .select('id,name,role,position,department,avatar_color,avatar_text_color,reports_to')
      .eq('org_id', org.id).eq('is_active', true).order('name')
      .then(({ data }) => { setProfiles(data || []); setLoading(false); });
  }

  useEffect(() => { loadProfiles(); }, [org, isOwner]); // eslint-disable-line react-hooks/exhaustive-deps

  function onDragStart(e, id) { e.dataTransfer.effectAllowed = 'move'; dragIdRef.current = id; setDragId(id); }
  function onDragOver(id)     { setDropTargetId(id); }
  function onDragEnd()        { dragIdRef.current = null; setDragId(null); setDropTargetId(null); }

  async function onDrop(targetId) {
    const id = dragIdRef.current || dragId;
    if (!id || id === targetId) { onDragEnd(); return; }
    setSaving(true);
    const { data: updated, error } = await supabase
      .from('profiles').update({ reports_to: targetId }).eq('id', id).select('id,name');
    dragIdRef.current = null;
    setDragId(null); setDropTargetId(null); setSaving(false);
    if (error) { show(`DB error: ${error.message}`, 'error'); return; }
    if (!updated?.length) { show('Update blocked — run the SQL fix in Supabase (see console).', 'error'); console.error('RLS blocked update: reports_to not changed for id', id); return; }
    loadProfiles();
  }

  async function onDropUnassigned(e) {
    e.preventDefault();
    const id = dragIdRef.current || dragId;
    if (!id) return;
    setSaving(true);
    const { data: updated, error } = await supabase
      .from('profiles').update({ reports_to: null }).eq('id', id).select('id,name');
    dragIdRef.current = null;
    setDragId(null); setDropTargetId(null); setSaving(false);
    if (error) { show(`DB error: ${error.message}`, 'error'); return; }
    if (!updated?.length) { show('Update blocked — run the SQL fix in Supabase.', 'error'); return; }
    loadProfiles();
  }

  if (!isOwner) return null;
  if (loading)  return <LoadingScreen />;

  const owner = profiles.find(p => p.role === 'owner');

  const childrenOf = {};
  profiles.forEach(p => {
    if (p.role === 'owner') return;
    if (p.role === 'manager' && (!p.reports_to || p.reports_to === owner?.id)) {
      if (owner) (childrenOf[owner.id] = childrenOf[owner.id] || []).push(p);
    } else {
      const key = p.reports_to || '__root__';
      (childrenOf[key] = childrenOf[key] || []).push(p);
    }
  });

  const unassigned = (childrenOf['__root__'] || []).filter(p => p.role === 'employee');
  async function handlePromoteConfirm() {
    if (!promoteTarget) return;
    const newRole = promoteTarget.role === 'employee' ? 'manager' : 'employee';
    setSaving(true);
    await supabase.from('profiles').update({ role: newRole }).eq('id', promoteTarget.id);
    if (newRole === 'employee') {
      await supabase.from('profiles').update({ reports_to: null }).eq('reports_to', promoteTarget.id);
    }
    setPromoteTarget(null); setSaving(false);
    loadProfiles();
  }

  async function handleSwapConfirm() {
    if (!swapTarget || !swapWith) return;
    setSaving(true);
    await Promise.all([
      supabase.from('profiles').update({ reports_to: swapWith.id  }).eq('reports_to', swapTarget.id),
      supabase.from('profiles').update({ reports_to: swapTarget.id }).eq('reports_to', swapWith.id),
    ]);
    setSwapTarget(null); setSwapWith(null); setSaving(false);
    loadProfiles();
  }

  const managers = profiles.filter(p => p.role === 'manager');
  const dndProps = { dragId, dropTargetId, onDragStart, onDragOver, onDrop, onDragEnd, onPromote: setPromoteTarget, onSwap: setSwapTarget, isOwner };

  return (
    <div className="page">
      <Toast toast={toast} />
      <PageHeader title="Org hierarchy" subtitle={`${profiles.length} active members`} />
      {isOwner && (
        <p style={{ fontSize:12.5, color:'var(--muted)', marginBottom:12 }}>
          Drag any person onto a manager to change who they report to. Use "↑ Make manager" to promote someone.
        </p>
      )}
      <Card style={{ opacity: saving ? 0.6 : 1, transition:'opacity 0.2s' }}>
        {owner ? (
          /* Outer wrapper catches drops that miss a specific node (dead zones), falling back to owner */
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); const id = dragIdRef.current || dragId; if (id && id !== owner.id) onDrop(owner.id); }}
          >
            <TreeNode profile={owner} childrenOf={childrenOf} {...dndProps} />
          </div>
        ) : (
          <EmptyState icon="🏢" title="No data yet" message="Members appear here as they join." />
        )}
      </Card>
      <Card
        style={{
          marginTop: 16,
          border: dropTargetId === '__unassigned__' ? '2px dashed var(--accent)' : '2px dashed var(--border)',
          background: dropTargetId === '__unassigned__' ? 'var(--accent-light,#f0f0ff)' : undefined,
          transition: 'border-color 0.15s',
        }}
        onDragOver={(e) => { e.preventDefault(); setDropTargetId('__unassigned__'); }}
        onDrop={onDropUnassigned}
        onDragLeave={() => setDropTargetId(null)}
      >
        <div className="card-header"><div className="card-title">Unassigned</div></div>
        {unassigned.length === 0
          ? <p style={{ fontSize:13, color:'var(--muted)', padding:'8px 0' }}>Drop someone here to remove their manager assignment.</p>
          : unassigned.map(p => (
            <div key={p.id} className="list-item"
              draggable={isOwner}
              onDragStart={(e) => onDragStart(e, p.id)}
              onDragEnd={onDragEnd}
              style={{ cursor:'grab' }}
            >
              <Avatar name={p.name} color={p.avatar_color} textColor={p.avatar_text_color} size="sm" />
              <div className="list-info">
                <div className="list-title">{p.name}</div>
                <div className="list-meta">{p.role} · {p.position}</div>
              </div>
              <span style={{ fontSize:11, color:'var(--muted)', opacity:0.5 }}>⠿</span>
            </div>
          ))
        }
      </Card>

      {/* Swap teams confirm */}
      <Modal isOpen={!!swapTarget} onClose={() => { setSwapTarget(null); setSwapWith(null); }} title="Swap teams">
        {swapTarget && (
          <>
            <p style={{ fontSize:13.5, color:'var(--muted)', marginBottom:16 }}>
              Choose a manager to swap teams with <strong>{swapTarget.name}</strong>. Their direct reports will exchange — no one is added or removed, just reassigned.
            </p>
            <FormRow label="Swap with">
              <Select value={swapWith?.id || ''} onChange={e => setSwapWith(managers.find(m => m.id === e.target.value) || null)}>
                <option value="">Select a manager…</option>
                {managers.filter(m => m.id !== swapTarget.id).map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </Select>
            </FormRow>
            <div className="modal-actions">
              <Btn onClick={() => { setSwapTarget(null); setSwapWith(null); }}>Cancel</Btn>
              <Btn variant="primary" onClick={handleSwapConfirm} disabled={!swapWith}>Swap teams</Btn>
            </div>
          </>
        )}
      </Modal>

      {/* Role promotion confirm */}
      <Modal isOpen={!!promoteTarget} onClose={() => setPromoteTarget(null)} title={promoteTarget?.role === 'employee' ? 'Promote to manager?' : 'Change to employee?'}>
        {promoteTarget && (
          <>
            <div style={{ display:'flex', alignItems:'center', gap:12, background:'var(--bg)', borderRadius:'var(--radius-sm)', padding:14, marginBottom:16 }}>
              <Avatar name={promoteTarget.name} color={promoteTarget.avatar_color} textColor={promoteTarget.avatar_text_color} size="lg" />
              <div><div style={{ fontWeight:600 }}>{promoteTarget.name}</div><div style={{ fontSize:13, color:'var(--muted)' }}>Currently: {promoteTarget.role}</div></div>
            </div>
            <p style={{ fontSize:13.5, color:'var(--muted)', marginBottom:16 }}>
              {promoteTarget.role === 'employee'
                ? `${promoteTarget.name} will become a manager and can schedule and invite employees.`
                : `${promoteTarget.name} will become an employee. Any people who report to them will become unassigned.`}
            </p>
            <div className="modal-actions">
              <Btn onClick={() => setPromoteTarget(null)}>Cancel</Btn>
              <Btn variant="primary" onClick={handlePromoteConfirm}>Confirm</Btn>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

// ── Manager Availability ──────────────────────────────────────────────────────
export function ManagerAvailability() {
  const { toast, show } = useToast();
  const { availability, loading, saveAvailability } = useAvailability();
  const [rows, setRows] = useState(
    AVAIL_DAYS.map(d => ({ day_of_week: d.dow, is_available: false, start_time: '09:00', end_time: '17:00' }))
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (loading) return;
    const map = {};
    availability.forEach(a => { map[a.day_of_week] = a; });
    setRows(AVAIL_DAYS.map(({ dow }) => ({
      day_of_week:  dow,
      is_available: map[dow]?.is_available ?? false,
      start_time:   (map[dow]?.start_time || '09:00:00').slice(0, 5),
      end_time:     (map[dow]?.end_time   || '17:00:00').slice(0, 5),
    })));
  }, [availability, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try { await saveAvailability(rows); show('Availability saved!'); }
    catch (err) { show(err.message, 'error'); }
    finally { setSaving(false); }
  }

  return (
    <div className="page">
      <Toast toast={toast} />
      <PageHeader title="My availability" subtitle="Set the days and times you're available to be scheduled." />
      <Card>
        {loading ? <Spinner /> : (
          <form onSubmit={handleSave}>
            {AVAIL_DAYS.map(({ label, dow }) => {
              const row = rows.find(r => r.day_of_week === dow);
              return (
                <div key={dow} className="avail-card">
                  <span style={{ width:110, fontWeight:500, fontSize:13.5 }}>{label}</span>
                  <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer' }}>
                    <input type="checkbox" checked={row.is_available}
                      onChange={e => setRows(prev => prev.map(r => r.day_of_week === dow ? { ...r, is_available: e.target.checked } : r))}
                      style={{ width:15, height:15, accentColor:'var(--accent)' }} />
                    <span style={{ fontSize:13 }}>{row.is_available ? 'Available' : 'Not available'}</span>
                  </label>
                  {row.is_available && (
                    <>
                      <Input type="time" step="900" value={row.start_time}
                        onChange={e => setRows(prev => prev.map(r => r.day_of_week === dow ? { ...r, start_time: e.target.value } : r))}
                        style={{ width:130 }} />
                      <span style={{ fontSize:13, color:'var(--muted)' }}>to</span>
                      <Input type="time" step="900" value={row.end_time}
                        onChange={e => setRows(prev => prev.map(r => r.day_of_week === dow ? { ...r, end_time: e.target.value } : r))}
                        style={{ width:130 }} />
                    </>
                  )}
                </div>
              );
            })}
            <div style={{ marginTop:20 }}>
              <Btn type="submit" variant="primary" disabled={saving}>{saving ? <Spinner /> : 'Save availability'}</Btn>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
}

// ── Notifications ─────────────────────────────────────────────────────────────
export function ManagerNotifications() {
  const { notifications, unreadCount, markAllRead } = useNotifications();
  return (
    <div className="page">
      <PageHeader title="Notifications" subtitle={`${unreadCount} unread`}
        action={unreadCount>0 && <Btn size="sm" onClick={markAllRead}>Mark all read</Btn>}
      />
      <Card>
        {notifications.length===0
          ? <EmptyState icon="🔔" title="All caught up" message="No notifications yet." />
          : notifications.map(n => (
            <div key={n.id} className="notif-item">
              <div className={`notif-dot ${n.read?'read':''}`}></div>
              <div style={{flex:1}}>
                <div className={`notif-text ${!n.read?'unread':''}`}>{n.text}</div>
                <div className="notif-time">{new Date(n.created_at).toLocaleString()}</div>
              </div>
            </div>
          ))
        }
      </Card>
    </div>
  );
}

// ── Analytics ─────────────────────────────────────────────────────────────────
export function Analytics() {
  const { org } = useAuth();
  if (!planAtLeast(org?.plan, 'growth')) return <UpgradePrompt requiredPlan="growth" />;
  return <AnalyticsContent />;
}

const CHART_COLORS = ['#4f46e5','#06b6d4','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899'];

function AStatCard({ label, value, sub, gradient }) {
  return (
    <div className="a-stat-card" style={{
      background: gradient,
      borderRadius: 'var(--radius)',
      padding: '20px 20px 16px',
      color: '#fff',
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
    }}>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, opacity: 0.8 }}>{label}</div>
      <div style={{ fontSize: 32, fontWeight: 800, lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, opacity: 0.75 }}>{sub}</div>}
    </div>
  );
}

function AnalyticsContent() {
  const { data, loading, reload } = useAnalytics();

  if (loading) return <LoadingScreen />;

  if (!data || data.totalShifts === 0) {
    return (
      <div className="page">
        <PageHeader title="Analytics" action={<Btn size="sm" onClick={reload}>Refresh</Btn>} />
        <EmptyState icon="📊" title="No shifts this month" message="Schedule some shifts and they'll show up here." />
      </div>
    );
  }

  const maxShifts = Math.max(...data.employeeBreakdown.map(e => e.shiftCount), 1);

  return (
    <div className="page">
      <PageHeader title="Analytics" subtitle={data.monthLabel}
        action={<Btn size="sm" onClick={reload}>Refresh</Btn>}
      />

      {/* KPI cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:14, marginBottom:20 }}>
        <AStatCard label="Total shifts"       value={data.totalShifts}           gradient="linear-gradient(135deg,#4f46e5,#7c3aed)" />
        <AStatCard label="Total hours"        value={`${data.totalHours}h`}      gradient="linear-gradient(135deg,#0891b2,#06b6d4)" />
        <AStatCard label="Avg hrs / employee" value={`${data.avgHoursPerEmp}h`}  gradient="linear-gradient(135deg,#059669,#10b981)" />
        <AStatCard label="Team coverage"      value={`${data.coveragePct}%`}     gradient="linear-gradient(135deg,#d97706,#f59e0b)" sub="of active employees scheduled" />
        <AStatCard label="Shift fill rate"    value={`${data.fillRate}%`}        gradient="linear-gradient(135deg,#dc2626,#ef4444)" sub={`${data.filledShifts} of ${data.postedShifts} open shifts filled`} />
      </div>

      {/* Charts row */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:16, marginBottom:16 }}>

        {/* Shifts by day of week */}
        <Card>
          <div className="card-header"><div className="card-title">Shifts by day</div></div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.shiftsByDay} margin={{ top:8, right:8, left:-20, bottom:0 }}>
              <XAxis dataKey="name" tick={{ fontSize:11 }} />
              <YAxis tick={{ fontSize:11 }} allowDecimals={false} />
              <Tooltip formatter={(v) => [v, 'Shifts']} />
              <Bar dataKey="shifts" radius={[4,4,0,0]}>
                {data.shiftsByDay.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Shifts by department */}
        {data.shiftsByDept.length > 0 && (
          <Card>
            <div className="card-header"><div className="card-title">By department</div></div>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={data.shiftsByDept} dataKey="value" nameKey="name" cx="50%" cy="50%"
                  outerRadius={70} label={({ name, percent }) => `${name} ${Math.round(percent*100)}%`}
                  labelLine={false} fontSize={11}>
                  {data.shiftsByDept.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [v + ' shifts', n]} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* Shifts by position */}
        {data.shiftsByPosition.length > 0 && (
          <Card>
            <div className="card-header"><div className="card-title">Top positions</div></div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.shiftsByPosition} layout="vertical" margin={{ top:4, right:16, left:8, bottom:0 }}>
                <XAxis type="number" tick={{ fontSize:11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize:11 }} width={80} />
                <Tooltip formatter={(v) => [v, 'Shifts']} />
                <Bar dataKey="value" radius={[0,4,4,0]}>
                  {data.shiftsByPosition.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}
      </div>

      {/* Employee breakdown */}
      <Card>
        <div className="card-header"><div className="card-title">Employee breakdown</div></div>
        <div style={{ padding:'8px 16px 16px', display:'flex', flexDirection:'column', gap:12 }}>
          {data.employeeBreakdown.map(e => (
            <div key={e.id} style={{ display:'flex', alignItems:'center', gap:12 }}>
              <Avatar name={e.name} color={e.avatarColor} textColor={e.avatarText} size="sm" />
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                  <span style={{ fontWeight:600, fontSize:13.5 }}>{e.name}</span>
                  <span style={{ fontSize:12, color:'var(--muted)' }}>{e.shiftCount} shift{e.shiftCount!==1?'s':''} · {e.totalHours}h</span>
                </div>
                <div style={{ height:6, background:'var(--border)', borderRadius:99, overflow:'hidden' }}>
                  <div style={{
                    height:'100%',
                    width: `${Math.round((e.shiftCount / maxShifts) * 100)}%`,
                    background:'linear-gradient(90deg,#4f46e5,#7c3aed)',
                    borderRadius:99,
                    transition:'width 0.4s ease',
                  }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ── Schedule History ──────────────────────────────────────────────────────────
export function ScheduleHistory() {
  const { org } = useAuth();
  if (!planAtLeast(org?.plan, 'starter')) return <UpgradePrompt requiredPlan="starter" />;
  return <ScheduleHistoryContent />;
}

function ScheduleHistoryContent() {
  const { schedules, loading, reload } = useScheduleHistory();
  const [selectedWeek, setSelectedWeek] = useState(null);

  if (loading) return <LoadingScreen />;

  if (selectedWeek) return <ScheduleHistoryDetail weekStart={selectedWeek} onBack={() => setSelectedWeek(null)} />;

  return (
    <div className="page">
      <PageHeader title="Schedule history" subtitle={`${schedules.length} published week${schedules.length !== 1 ? 's' : ''}`}
        action={<Btn size="sm" onClick={reload}>Refresh</Btn>}
      />
      <Card>
        {schedules.length === 0
          ? <EmptyState icon="🕐" title="No published schedules" message="Published schedules will appear here." />
          : (
            <div className="table-wrapper">
              <table className="table">
                <thead><tr><th>Week</th><th>Published</th><th></th></tr></thead>
                <tbody>
                  {schedules.map(s => {
                    const ws = getWeekStart(new Date(s.week_start + 'T12:00:00'));
                    return (
                      <tr key={s.id}>
                        <td>{fmtWeekRange(ws)}</td>
                        <td>{s.published_at ? new Date(s.published_at).toLocaleDateString() : '—'}</td>
                        <td><Btn size="sm" onClick={() => setSelectedWeek(s.week_start)}>View</Btn></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        }
      </Card>
    </div>
  );
}

function ScheduleHistoryDetail({ weekStart, onBack }) {
  const ws   = getWeekStart(new Date(weekStart + 'T12:00:00'));
  const we   = addDays(ws, 6);
  const { shifts, loading } = useShifts(toISO(ws), toISO(we));
  const week = buildWeek(ws);

  if (loading) return <LoadingScreen />;

  const shiftsByDate = {};
  shifts.forEach(s => { (shiftsByDate[s.shift_date] = shiftsByDate[s.shift_date] || []).push(s); });

  return (
    <div className="page">
      <PageHeader title={`Week of ${fmtWeekRange(ws)}`}
        action={<Btn size="sm" onClick={onBack}>← Back</Btn>}
      />
      <Card style={{marginBottom:16,padding:'18px 16px'}}>
        <div className="schedule-scroll">
          <div className="week-grid">
            {week.map(({ key, date, num }) => {
              const dayShifts = shiftsByDate[date] || [];
              return (
                <div key={key} className="day-col">
                  <div className="day-header">{key}</div>
                  <div className="day-date">{num}</div>
                  {dayShifts.length === 0
                    ? <div className="day-off">No shifts</div>
                    : dayShifts.map(s => (
                      <div key={s.id} className={`shift-block ${colorFor(s.employee_id)}`}>
                        <div className="sb-name">{s.employee?.name?.split(' ')[0]}</div>
                        <div className="sb-pos">{s.position}</div>
                        <div className="sb-time">{fmtTime(s.start_time)}–{fmtTime(s.end_time)}</div>
                      </div>
                    ))
                  }
                </div>
              );
            })}
          </div>
        </div>
      </Card>
      <Card>
        <div className="card-header"><div className="card-title">Shifts ({shifts.length})</div></div>
        <div className="table-wrapper">
          <table className="table">
            <thead><tr><th>Employee</th><th>Position</th><th>Date</th><th>Start</th><th>End</th><th>Hrs</th></tr></thead>
            <tbody>
              {shifts.map(s => (
                <tr key={s.id}>
                  <td><div style={{display:'flex',alignItems:'center',gap:8}}><Avatar name={s.employee?.name||'?'} color={s.employee?.avatar_color} textColor={s.employee?.avatar_text_color} size="sm" />{s.employee?.name}</div></td>
                  <td>{s.position}</td>
                  <td>{fmtDate(s.shift_date)}</td>
                  <td>{fmtTime(s.start_time)}</td>
                  <td>{fmtTime(s.end_time)}</td>
                  <td>{s.duration_hours}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ── Department Manager ─────────────────────────────────────────────────────────
const DEPT_COLORS = ['#6366f1','#ec4899','#f59e0b','#10b981','#3b82f6','#8b5cf6','#ef4444','#06b6d4','#64748b','#f97316'];

export function DepartmentManager() {
  const { org } = useAuth();
  const { toast, show } = useToast();
  const { departments, reload } = useDepartments();

  const [editDept, setEditDept]   = useState(null);
  const [addForm, setAddForm]     = useState({ name: '', color: DEPT_COLORS[0] });
  const [adding, setAdding]       = useState(false);
  const [saving, setSaving]       = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const displayDepts = departments.filter(d => !d.is_any).sort((a,b) => a.display_order - b.display_order);
  const anyDept = departments.find(d => d.is_any);

  async function handleAdd(e) {
    e.preventDefault();
    if (!addForm.name.trim()) return;
    setAdding(true);
    try {
      await supabase.from('departments').insert({
        org_id: org.id, name: addForm.name.trim(), color: addForm.color,
        display_order: displayDepts.length,
      });
      setAddForm({ name: '', color: DEPT_COLORS[displayDepts.length % DEPT_COLORS.length] });
      show('Department added!'); reload();
    } catch(err) { show(err.message, 'error'); }
    finally { setAdding(false); }
  }

  async function handleEditSave() {
    if (!editDept || !editDept.name.trim()) return;
    setSaving(true);
    try {
      await supabase.from('departments').update({ name: editDept.name.trim(), color: editDept.color }).eq('id', editDept.id);
      show('Saved!'); setEditDept(null); reload();
    } catch(err) { show(err.message, 'error'); }
    finally { setSaving(false); }
  }

  async function handleDelete() {
    if (!confirmDel) return;
    try {
      await supabase.from('departments').delete().eq('id', confirmDel.id);
      show(`${confirmDel.name} removed.`); setConfirmDel(null); reload();
    } catch(err) { show(err.message, 'error'); }
  }

  async function handleReorder(dept, dir) {
    const idx = displayDepts.findIndex(d => d.id === dept.id);
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= displayDepts.length) return;
    const other = displayDepts[swapIdx];
    await Promise.all([
      supabase.from('departments').update({ display_order: other.display_order }).eq('id', dept.id),
      supabase.from('departments').update({ display_order: dept.display_order }).eq('id', other.id),
    ]);
    reload();
  }

  function ColorSwatches({ selected, onChange }) {
    return (
      <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
        {DEPT_COLORS.map(c => (
          <div key={c} onClick={() => onChange(c)} style={{
            width:20, height:20, borderRadius:'50%', background:c, cursor:'pointer', flexShrink:0,
            border: selected === c ? '2px solid var(--text)' : '2px solid transparent',
          }} />
        ))}
      </div>
    );
  }

  return (
    <div className="page">
      <Toast toast={toast} />
      <PageHeader title="Departments" subtitle="Add, rename, reorder, or remove your organization's departments." />

      <Card style={{ marginBottom:16 }}>
        <div className="card-header"><div className="card-title">Sections</div></div>

        {/* Locked "Any" row */}
        {anyDept && (
          <div style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 0', borderBottom:'1px solid var(--border)' }}>
            <div style={{ width:14, height:14, borderRadius:'50%', background:'#64748b', flexShrink:0 }} />
            <div style={{ flex:1, fontWeight:500, color:'var(--muted)' }}>Any</div>
            <span style={{ fontSize:12, color:'var(--muted)', fontStyle:'italic' }}>always included — cannot be removed</span>
          </div>
        )}

        {displayDepts.length === 0 && (
          <div style={{ textAlign:'center', padding:'20px 0', color:'var(--muted)', fontSize:13.5 }}>
            No departments yet — add one below.
          </div>
        )}

        {displayDepts.map((d, idx) => (
          <div key={d.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 0', borderBottom:'1px solid var(--border)', flexWrap:'wrap' }}>
            <div style={{ width:14, height:14, borderRadius:'50%', background: editDept?.id === d.id ? editDept.color : d.color, flexShrink:0 }} />
            {editDept?.id === d.id ? (
              <>
                <Input value={editDept.name} onChange={e => setEditDept(p => ({...p, name:e.target.value}))} style={{ flex:'1 1 140px', minWidth:120 }} />
                <ColorSwatches selected={editDept.color} onChange={c => setEditDept(p => ({...p, color:c}))} />
                <Btn size="sm" variant="primary" onClick={handleEditSave} disabled={saving}>{saving ? <Spinner /> : 'Save'}</Btn>
                <Btn size="sm" onClick={() => setEditDept(null)}>Cancel</Btn>
              </>
            ) : (
              <>
                <div style={{ flex:1, fontWeight:500 }}>{d.name}</div>
                <div style={{ display:'flex', gap:4, alignItems:'center' }}>
                  <button onClick={() => handleReorder(d,-1)} disabled={idx===0}
                    style={{ background:'none', border:'1px solid var(--border)', borderRadius:4, cursor: idx===0?'default':'pointer', padding:'2px 8px', color:'var(--muted)', opacity: idx===0?0.35:1 }}>↑</button>
                  <button onClick={() => handleReorder(d,1)} disabled={idx===displayDepts.length-1}
                    style={{ background:'none', border:'1px solid var(--border)', borderRadius:4, cursor: idx===displayDepts.length-1?'default':'pointer', padding:'2px 8px', color:'var(--muted)', opacity: idx===displayDepts.length-1?0.35:1 }}>↓</button>
                  <Btn size="sm" variant="ghost" onClick={() => setEditDept({ id:d.id, name:d.name, color:d.color })}>Edit</Btn>
                  <Btn size="sm" variant="ghost" style={{ color:'var(--red,#e03131)' }} onClick={() => setConfirmDel(d)}>Delete</Btn>
                </div>
              </>
            )}
          </div>
        ))}

        {/* Add new */}
        <form onSubmit={handleAdd} style={{ display:'flex', gap:10, alignItems:'center', paddingTop:14, flexWrap:'wrap' }}>
          <Input value={addForm.name} onChange={e => setAddForm(f => ({...f, name:e.target.value}))}
            placeholder="New department name" style={{ flex:'1 1 160px', minWidth:140 }} required />
          <ColorSwatches selected={addForm.color} onChange={c => setAddForm(f => ({...f, color:c}))} />
          <Btn type="submit" variant="primary" size="sm" disabled={adding}>{adding ? <Spinner /> : '+ Add'}</Btn>
        </form>
      </Card>

      <Modal isOpen={!!confirmDel} onClose={() => setConfirmDel(null)} title="Delete department?">
        {confirmDel && (
          <>
            <p style={{ fontSize:13.5, color:'var(--muted)', marginBottom:16 }}>
              Deleting <strong>{confirmDel.name}</strong> removes it from all team needs slots and employee assignments. Existing shifts are not affected.
            </p>
            <div className="modal-actions">
              <Btn onClick={() => setConfirmDel(null)}>Cancel</Btn>
              <Btn variant="danger" onClick={handleDelete}>Delete</Btn>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
